import React, { useState, useEffect, useMemo, useContext } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import apiConfig from "../../config/apiConfig";
import { apiFetch } from "../../apis/apiFetch";
import { getConfig } from "../../config/runtimeConfig";
import { toast } from "../../utils/toast";
import SidebarAdmin from "../Utils/SidebarAdmin";
import LogoutButton from "../LogoutButton";
import RenderImage from "../QueryManager/RenderImage/RenderImage";
import { LoginContext } from "../../context/LoginContext";
import { queryAttachmentService } from "../../services/RenderImageService";
import "../Applications.css";
import * as XLSX from "xlsx";
import SendEmail from "../SendEmail/SendEmail";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

export const formatDateDDMMYYYY = (dateValue: any) => {
  if (!dateValue) return "No Date";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "No Date";
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const renderCircleStatus = (status: string | boolean | undefined) => {
  let type: "success" | "error" | "pending" | "editing" | "resubmitted" = "pending";
  let title = "Pending";

  if (status === true || status === "ACCEPTED") {
    type = "success";
    title = "Accepted";
  } else if (status === false || status === "NOT_ACCEPTED") {
    type = "error";
    title = "Not Accepted";
  } else if (status === "JEE_EDITING") {
    type = "editing";
    title = "JEE Editing";
  } else if (status === "JEE_MAIN_RESUBMITTED") {
    type = "resubmitted";
    title = "JEE Main Resubmitted";
  } else if (status === "JEE_ADVANCED_RESUBMITTED") {
    type = "resubmitted";
    title = "JEE Advanced Resubmitted";
  }

  const colors = {
    success: { bg: "#e6f4ea", text: "#137333", border: "#ceead6", char: "✓" },
    error: { bg: "#fce8e6", text: "#c5221f", border: "#fad2cf", char: "✗" },
    pending: { bg: "#f1f3f4", text: "#5f6368", border: "#dadce0", char: "—" },
    editing: { bg: "#fef3c7", text: "#b45309", border: "#fde68a", char: "JEE_EDITING" },
    resubmitted: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", char: "RESUBMITTED" },
  };

  const config = colors[type];

  if (type === "editing" || type === "resubmitted") {
    return (
      <span
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: config.bg,
          color: config.text,
          border: `1px solid ${config.border}`,
          borderRadius: "4px",
          padding: "2px 6px",
          fontSize: "11px",
          fontWeight: "bold",
          lineHeight: 1,
        }}
      >
        {config.char}
      </span>
    );
  }

  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "22px",
        height: "22px",
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        borderRadius: "50%",
        fontSize: "12px",
        fontWeight: "bold",
        lineHeight: 1,
      }}
    >
      {config.char}
    </span>
  );
};

export interface Application {
  id: string;
  user_id: string;
  application_number: string;
  status: string;
  pref1?: string;
  pref2?: string;
  pref3?: string;
  pref4?: string;
  pref5?: string;
  pref1score?: number;
  pref2score?: number;
  pref3score?: number;
  pref1rollno?: string;
  pref2rollno?: string;
  pref3rollno?: string;
  pref1year?: string;
  pref2year?: string;
  pref3year?: string;
  drive_id: string;
  offered_payment_status: string;
  onlineRound?: string;
  extra_data?: {
    user_name?: string;
    user_email?: string;
    user_mobile?: string;
    drive_name?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface Round {
  id: string;
  name: string;
  startdate?: string;
  enddate?: string;
  withdrawal_end_date?: string;
  date?: string;
  drive_id?: string;
  round_type?: string
  round_number?: number;
  allocation_mode?: string
  extra_data?: Record<string, any>;
  btcse_exhausted?: boolean;
  btece_exhausted?: boolean;
  btaids_exhausted?: boolean;
  imtcse_exhausted?: boolean;
  imtece_exhausted?: boolean;
  mtcse_exhausted?: boolean;
  mtece_exhausted?: boolean;
  mtaids_exhausted?: boolean;
  btech_online_cse_remaining?: number
  btech_online_ece_remaining?: number
  btech_online_aids_remaining?: number
  imtech_online_cse_remaining?: number
  imtech_online_ece_remaining?: number
  btech_online_cse_seats?: number
  btech_online_ece_seats?: number
  btech_online_aids_seats?: number
  imtech_online_cse_seats?: number
  imtech_online_ece_seats?: number
  last_updated_at?: string
  btech_online_cse_last_updated?: string
  btech_online_ece_last_updated?: string
  btech_online_aids_last_updated?: string
  imtech_online_cse_last_updated?: string
  imtech_online_ece_last_updated?: string
  mtech_online_cse_seats?: number
  mtech_online_cse_remaining?: number
  mtech_online_cse_last_updated?: string
  mtech_online_ece_seats?: number
  mtech_online_ece_remaining?: number
  mtech_online_ece_last_updated?: string
  mtech_online_aids_seats?: number
  mtech_online_aids_remaining?: number
  mtech_online_aids_last_updated?: string
}

// ── MTech popup config ──────────────────────────────────────────────
const MTECH_SECTION_LABELS: Record<string, string> = {
  personal_details: "Personal Details",
  current_address: "Current Address",
  permanent_address: "Permanent Address",
  guardian_details: "Parent/Guardian Details",
  graduation_details: "Graduation Details",
  programme_preference: "Programme Preference",
  gate_details: "GATE Details",
  payment: "Payment Details",
  offered_payment: "Round Payment Details",
};
const MTECH_SECTION_FIELD_ORDER: Record<string, string[]> = {
  personal_details: ["fullname", "email", "mobile", "dob", "gender", "nationality", "photo", "abc_id"],
  current_address: ["line1", "line2", "city", "state", "pincode", "country"],
  permanent_address: ["line1", "line2", "city", "state", "pincode", "country"],
  guardian_details: ["guardian_name", "guardian_relation", "guardian_mobile", "guardian_email"],
  graduation_details: ["collegename", "degree", "major", "cgpa", "passingyear", "marks_obtained", "maximum_marks"],
  programme_preference: ["pref1", "pref2", "pref3"],
  gate_details: ["subjects", "score", "rank", "rollno", "year", "scorecard"],
  payment: ["transaction_id", "amount", "status", "payment_date", "payment_mode", "reference_no"],
  offered_payment: ["offered_payment_status"]
};

// ── BTech popup config ──────────────────────────────────────────────
const BTECH_SECTION_LABELS: Record<string, string> = {
  personal_details: "Personal Details",
  current_address: "Current Address",
  permanent_address: "Permanent Address",
  guardian_details: "Parent/Guardian Details",
  education_details: "Education Details (10th & 12th)",
  exam_details: "Exam Details",
  programme_preference: "Programme Preference",
  payment: "Payment Details",
  offered_payment: "Offered Payment Details",
};
const BTECH_SECTION_FIELD_ORDER: Record<string, string[]> = {
  personal_details: ["fullname", "email", "mobile", "dob", "gender", "nationality", "photo", "abc_id"],
  current_address: ["line1", "line2", "city", "state", "pincode", "country"],
  permanent_address: ["line1", "line2", "city", "state", "pincode", "country"],
  guardian_details: ["guardian_name", "guardian_relation", "guardian_mobile", "guardian_email"],
  education_details: [
    "edu10_board", "edu10_schoolname", "edu10_passingyear", "edu10_marksformat",
    "edu10_marksobtained", "edu10_maximummarks", "edu10_percentage",
    "edu12_board", "edu12_schoolname", "edu12_passingyear", "edu12_percentagemaths",
    "edu12_marksformat", "edu12_marksobtained", "edu12_maximummarks", "edu12_percentage",
  ],
  exam_details: [
    "jeemain_rollno", "jeemain_score", "jeemain_rank", "jeemain_scorecard",
    "jeeadv_rollno", "jeeadv_score", "jeeadv_rank", "jeeadv_scorecard",
    "olympiad_name", "olympiad_no", "olympiad_proof", "olympiad_year",
  ],
  programme_preference: ["pref1", "pref2", "pref3", "pref4", "pref5"],
  payment: ["transaction_id", "amount", "status", "payment_date", "payment_mode", "reference_no"],
  offered_payment: ["offered_payment_status"]
};

// ── Shared field labels ─────────────────────────────────────────────
const FIELD_LABELS: Record<string, string> = {
  fullname: "Full Name", email: "Email", mobile: "Mobile", dob: "Date of Birth",
  gender: "Gender", photo: "Photo", nationality: "Nationality",
  line1: "Address Line 1", line2: "Address Line 2", city: "City", state: "State",
  pincode: "Pincode", country: "Country",
  name: "Name", relation: "Relation", alternatemobile: "Alternate Mobile",
  guardian_name: "Guardian Name", guardian_relation: "Guardian Relation",
  guardian_mobile: "Guardian Mobile", guardian_email: "Guardian Email",
  collegename: "College Name", degree: "Degree", major: "Branch",
  cgpa: "Percentage", passingyear: "Year of Passing",
  marks_obtained: "Marks / CGPA Obtained", maximum_marks: "Maximum Marks / CGPA",
  pref1: "Programme 1", pref2: "Programme 2", pref3: "Programme 3",
  pref4: "Programme 4", pref5: "Programme 5",
  pref1scorecard: "Programme 1 Scorecard", pref2scorecard: "Programme 2 Scorecard", pref3scorecard: "Programme 3 Scorecard",
  pref1rollno: "Programme 1 Roll No.", pref2rollno: "Programme 2 Roll No.", pref3rollno: "Programme 3 Roll No.",
  pref1score: "Programme 1 Score", pref2score: "Programme 2 Score", pref3score: "Programme 3 Score",
  pref1rank: "Programme 1 Rank", pref2rank: "Programme 2 Rank", pref3rank: "Programme 3 Rank",
  pref1subjects: "Programme 1 Subject", pref2subjects: "Programme 2 Subject", pref3subjects: "Programme 3 Subject",
  pref1year: "Programme 1 Year", pref2year: "Programme 2 Year", pref3year: "Programme 3 Year",
  subjects: "Subject", score: "GATE Score", rank: "GATE Rank", rollno: "GATE Roll No.", scorecard: "Scorecard",
  transaction_id: "Transaction ID", offered_payment_status: "Offered Payment Status",
  amount: "Amount", status: "Status", payment_date: "Payment Date",
  payment_mode: "Payment Mode", reference_no: "Reference No.", user_id: "User ID",
  // BTech edu
  edu10_board: "10th Board", edu10_schoolname: "10th School", edu10_passingyear: "10th Year of Passing",
  edu10_marksformat: "10th Grade/Marks Type", edu10_marksobtained: "10th Marks Obtained",
  edu10_maximummarks: "10th Maximum Marks", edu10_percentage: "10th Percentage",
  edu12_board: "12th Board", edu12_schoolname: "12th School", edu12_passingyear: "12th Year of Passing",
  edu12_percentagemaths: "12th Maths Percentage", edu12_marksformat: "12th Grade/Marks Type",
  edu12_marksobtained: "12th Marks Obtained", edu12_maximummarks: "12th Maximum Marks", edu12_percentage: "12th Percentage",
  // BTech exam
  jeemain_rollno: "JEE Main Application No.", jeemain_score: "JEE Main NTA Score",
  jeemain_rank: "JEE Main Rank (CRL)", jeemain_scorecard: "JEE Main Scorecard",
  jeeadv_rollno: "JEE Advanced Roll No.", jeeadv_score: "JEE Advanced Score",
  jeeadv_rank: "JEE Advanced Rank (CRL)", jeeadv_scorecard: "JEE Advanced Scorecard",
  olympiad_name: "Olympiad Name", olympiad_no: "Olympiad Roll No.",
  olympiad_proof: "Olympiad Proof", olympiad_year: "Olympiad Year",
};

const SKIP_FIELDS = new Set([
  "id", "drive_id", "user_id", "application_number", "payment_gateway",
  "offered_round_id", "offered_round_name", "offered_programme",
  "accepted_at", "offer_letter_doc_id", "offer_sent_date", "offer_status",
  "g_created_by_id", "g_created_by_name", "g_modified_by_id",
  "g_modified_by_name", "g_creation_time", "g_modify_time", "g_soft_delete",
  "g_status", "archived", "archived_time", "extra_data",
  "offer_status_1", "offer_status_2", "offer_status_3",
  "offered_programme_1", "offered_programme_2", "offered_programme_3",
  "iir", "verification_status", "payment_status",
]);

export default function ViewRounds() {
  const { user } = useContext(LoginContext);
  const navigate = useNavigate();
  const { type } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [drives, setDrives] = useState<any[]>([]);
  const [selectedRound, setSelectedRound] = useState<string>("");
  const [selectRoundType, setSelecteRoundType] = useState<string>("Offline");
  const [selectedRoundType, setSelectedRoundType] = useState<string>("");
  const [applications, setApplications] = useState<Application[]>([]);
  const [onlineRoundApplication, setOnlineRoundApplication] = useState<Application[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"POOL" | "PENDING_GEN" | "UPGRADED" | "PENDING_SEND" | "SENT" | "CANCELLED" | "ELIGIBLE" | "REGISTERED" | "TOKEN_EMAIL_SEND">("POOL");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [offeredPaymentStatusFilter, setOfferedPaymentStatusFilter] = useState<string>("ALL");
  const [isCancelling, setIsCancelling] = useState(false);
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showGenConfirmModal, setShowGenConfirmModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [genProgress, setGenProgress] = useState<{ jobId: string; done: number; failed: number; total: number; status: string } | null>(null);
  const [sendProgress, setSendProgress] = useState<{ jobId: string; done: number; failed: number; total: number; status: string } | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[] | null>([])
  const [showUpgradeConfirmModal, setShowUpgradeConfirmModal] = useState(false);
  const [isMarkingEligible, setIsMarkingEligible] = useState(false);
  const [showEligibleConfirmModal, setShowEligibleConfirmModal] = useState(false);
  const [isGeneratingTokens, setIsGeneratingTokens] = useState(false);
  const [showTokenConfirmModal, setShowTokenConfirmModal] = useState(false);
  const [isSendingTokenEmail, setIsSendingTokenEmail] = useState(false);
  const [showTokenEmailConfirmModal, setShowTokenEmailConfirmModal] = useState(false);
  // Whether the current round uses two-step token flow (generate then email separately)



  // Pagination State
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Popup State
  const [showPopup, setShowPopup] = useState(false);
  const [popupAppNumber, setPopupAppNumber] = useState<string>("");
  const [popupData, setPopupData] = useState<any>(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupIsBtech, setPopupIsBtech] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSelectAllModal, setShowSelectAllModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["personal_details", "current_address", "education_details", "exam_details", "graduation_details", "guardian_details"])
  );
  console.log("rounds", rounds)

  useEffect(() => {
    setApplications([])
    setSelectedRound("")
    setSelectedIds([])
    if (selectRoundType === "Online") {
      setActiveTab("ELIGIBLE")
    } else {
      setActiveTab("POOL")
    }
  }, [selectRoundType])

  useEffect(() => {
    if (type) {
      const mappedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      if (["Online", "Offline", "Manual"].includes(mappedType)) {
        setSelecteRoundType(mappedType);
      }
    }
  }, [type]);
  useEffect(() => {
    const fetchRounds = async () => {
      try {
        const url = `${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        let rawRounds = [];
        if (Array.isArray(data)) rawRounds = data;
        else if (data && Array.isArray(data.resource)) rawRounds = data.resource;
        else if (data && Array.isArray(data.records)) rawRounds = data.records;

        const sorted = rawRounds.sort((a: any, b: any) => {
          const parseDate = (val: any) => {
            if (!val) return 0;
            const d = new Date(val);
            if (!isNaN(d.getTime())) return d.getTime();
            const n = Number(val);
            if (!isNaN(n)) return n;
            return 0;
          };
          return parseDate(b.startdate || b.date) - parseDate(a.startdate || a.date);
        });
        setRounds(sorted);
      } catch (err) {
        console.error("ViewRounds: Error fetching rounds", err);
      }
    };
    const fetchDrives = async () => {
      try {
        const url = `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`;
        const response = await apiFetch(url);
        if (response.ok) {
          const data = await response.json();
          let rawDrives = [];
          if (Array.isArray(data)) rawDrives = data;
          else if (data && Array.isArray(data.resource)) rawDrives = data.resource;
          else if (data && Array.isArray(data.records)) rawDrives = data.records;
          setDrives(rawDrives);
        }
      } catch (err) {
        console.error("ViewRounds: Error fetching drives", err);
      }
    };
    fetchRounds();
    fetchDrives();
  }, []);

  useEffect(() => {
    const round = rounds.filter((round) => round.id === selectedRound)?.[0]
    if (round?.round_type) {
      setSelectedRoundType(round.round_type)
    }
  }, [selectedRound])

  const currentRoundObj = rounds.find(r => r.id === selectedRound);
  const isBtechRound = drives.find(d => d.id === currentRoundObj?.drive_id)?.drivetype?.toLowerCase() === "btech";
  const isMtechRound = drives.find(d => d.id === currentRoundObj?.drive_id)?.drivetype?.toLowerCase() === "mtech";
  const isOnline = currentRoundObj?.round_type === "Online";
  const isSlidingRound = currentRoundObj?.allocation_mode === "SLIDING";
  const isBtechOnlineRound2 = isBtechRound && isOnline && currentRoundObj?.round_number === 2;
  const isBtechOnlineRound3 = isBtechRound && isOnline && currentRoundObj?.round_number === 3;
  const isOnlineRound2 = isOnline && currentRoundObj?.round_number === 2;
  const isOnlineRound5 = isOnline && currentRoundObj?.round_number === 5;
  const isOnlineRound4 = isOnline && currentRoundObj?.round_number === 4;
  const isOnlineRound3 = isOnline && currentRoundObj?.round_number === 3;
  const isTwoStepTokenRound = (isOnlineRound5 && !isMtechRound) || (isOnlineRound4 && !isMtechRound) || (isOnlineRound3 && !isMtechRound) || (isOnlineRound2 && isMtechRound);
  const isFirstRound = (() => {
    if (!currentRoundObj || !currentRoundObj.drive_id) return true;
    const roundList = rounds.filter(r => r.drive_id === currentRoundObj.drive_id).sort((a, b) => new Date(a.startdate || a.date || 0).getTime() - new Date(b.startdate || b.date || 0).getTime());
    return roundList.length > 0 && roundList[0].id === currentRoundObj.id;
  })();

  const fetchApplications = async (roundId: string) => {
    setLoading(true);
    setApplications([]);
    setSelectedIds([]);
    try {
      const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_ROUND_APPLICATIONS&args=${encodeURIComponent(`round_id:${roundId}`)}`;
      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      let apps = [];
      if (Array.isArray(data)) apps = data;
      else if (data && Array.isArray(data.resource)) apps = data.resource;

      setApplications(apps as Application[]);
      if (apps.length === 0) {
        toast.info("No eligible applications found for this round.");
      }
    } catch (err) {
      console.error("ViewRounds: Error fetching applications", err);
      toast.error("Error fetching applications for this round.");
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationByIIR = async (iir: number): Promise<number | null> => {
    setLoading(true);

    try {
      let args = `iir:${iir}`;
      if (currentRoundObj?.drive_id) {
        args += `,drive_id:${currentRoundObj.drive_id}`;
      }
      if (selectedRound) {
        args += `,round_id:${selectedRound}`;
      }
      const url = `${apiConfig.getResourceUrl(
        "applications"
      )}?queryId=GET_APPLICATION_BY_IIR&args=${encodeURIComponent(args)}`;

      const response = await apiFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const apps = Array.isArray(data)
        ? data
        : Array.isArray(data?.resource)
          ? data.resource
          : [];

      const appId = apps?.[0]?.id;

      if (appId) {
        return appId;
      }

      toast.info("Application not found");
      return null;
    } catch (err) {
      console.error("Unable to load application", err);
      toast.error("Unable to load application");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onRoundChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const rId = e.target.value;
    setSelectedRound(rId);
    if (rId) {
      setPageNo(1);
      setActiveTab("POOL");
      fetchApplications(rId);
    } else {
      setApplications([]);
    }
  };

  const onRoundTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roundType = e.target.value;
    setSelecteRoundType(roundType);
    navigate(`/viewrounds/${roundType.toLowerCase()}`);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setShowSelectAllModal(true);
    } else {
      setSelectedIds([]);
    }
  };

  const handleRowSelect = (id: string, matchedProg: string, slidingStatus: string) => {
    const key = `${id}|${matchedProg}|${slidingStatus || ''}`;
    if (selectedIds.includes(key)) {
      setSelectedIds(selectedIds.filter((item) => item !== key));
    } else {
      setSelectedIds([...selectedIds, key]);
    }
  };

  const handleConfirmOffer = () => {
    if (!selectedIds || selectedIds.length === 0) {
      toast.error("Please select at least one application to confirm.");
      return;
    }
    setShowConfirmationModal(true);
  };

  const executeConfirmOffer = async () => {
    setIsConfirming(true);
    try {
      const roundObj = rounds.find((r) => r.id === selectedRound);
      const BATCH_SIZE = 30;
      let successCount = 0;
      let failedCount = 0;

      const rowKeys = [...selectedIds];

      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(k => k.split("|")[0]).join(";");
        const batchProgs = batch.map(k => k.split("|")[1]).join(";");
        const batchStatuses = batch.map(k => {
          const st = k.split("|")[2];
          return st ? st : "NONE";
        }).join(";");

        try {
          const args = `application_id:${batchAppIds},` +
            `offered_programme:${batchProgs},` +
            `sliding_status:${batchStatuses},` +
            `round_id:${selectedRound},` +
            `round_name:${roundObj?.name || ""},` +
            `is_online:${selectRoundType === "Online"}`;

          const url = `${apiConfig.getResourceUrl("applications")}?queryId=CONFIRM_CANDIDATES&args=${encodeURIComponent(args)}`;
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();

          if (res?.errCode === 0) {
            const results = res.resource || [];
            successCount += results.length;
          } else {
            failedCount += batch.length;
          }
        } catch (err) {
          failedCount += batch.length;
        }
        await new Promise((res) => setTimeout(res, 300));
      }

      toast.success(`Confirmed ${successCount} candidates successfully.`);
      if (failedCount > 0) {
        toast.error(`${failedCount} candidates failed confirmation.`);
      }

      setSelectedIds([]);
      if (selectedRound) fetchApplications(selectedRound);
    } catch (err) {
      console.error("Confirmation failed", err);
      toast.error("An error occurred during confirmation.");
    } finally {
      setIsConfirming(false);
      setShowConfirmationModal(false);
    }
  };

  const handleGenerateOffer = () => {
    if (!selectedIds || selectedIds.length === 0) {
      toast.error("Please select at least one application to generate offers for.");
      return;
    }
    setShowGenConfirmModal(true);
  };

  const executeGenerateOffer = async () => {
    setIsGenerating(true);
    setGenProgress(null);
    try {
      const roundObj = rounds.find((r) => r.id === selectedRound);
      const rowKeys = [...selectedIds];
      const total = rowKeys.length;

      // Batching to avoid 400 URL length errors (approx 40 candidates per batch)
      const BATCH_SIZE = 40;
      const batchJobs: { jobId: string, size: number }[] = [];
      let initialFailed = 0;

      // 1. Submit ALL batches immediately to backend so they run in background
      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(k => k.split("|")[0]).join(";");
        const batchProgs = batch.map(k => k.split("|")[1]).join(";");

        const args = `application_id:${batchAppIds},` +
          `offered_programme:${batchProgs},` +
          `offered_round_id:${selectedRound},` +
          `offered_round_name:${roundObj?.name || ""},` +
          `is_online:${selectRoundType === "Online"}`;

        const url = `${apiConfig.getResourceUrl("applications")}?queryId=GENERATE_OFFER_LETTER&args=${encodeURIComponent(args)}`;

        try {
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();

          const jobId = res?.resource?.[0]?.extra_data?.job_id;
          if (res?.errCode === 0 && jobId) {
            batchJobs.push({ jobId, size: batch.length });
          } else {
            initialFailed += batch.length;
          }
        } catch (err) {
          initialFailed += batch.length;
        }
      }

      if (batchJobs.length === 0) {
        toast.error("Failed to start generation for any candidates.");
        setIsGenerating(false);
        return;
      }

      // 2. Poll all active jobs every 10 seconds
      let allFinished = false;
      let cumulativeDone = 0;
      let cumulativeFailed = initialFailed;

      while (!allFinished) {
        let currentDone = 0;
        let currentFailed = initialFailed;
        let anyRunning = false;

        for (const job of batchJobs) {
          try {
            const statusUrl = `${apiConfig.getResourceUrl("applications")}?queryId=GET_JOB_STATUS&args=${encodeURIComponent(`job_id:${job.jobId}`)}`;
            const statusRes = await apiFetch(statusUrl, { method: "GET" });
            const statusData = await statusRes.json();
            const jobData = statusData?.resource?.[0]?.extra_data;

            if (!jobData || jobData.status === "NOT_FOUND") {
              currentFailed += job.size;
              continue;
            }

            currentDone += (jobData.done || 0);
            currentFailed += (jobData.failed || 0);

            if (jobData.status === "RUNNING") {
              anyRunning = true;
            }
          } catch (err) {
            anyRunning = true; // network error, assume running
          }
        }

        setGenProgress({
          jobId: batchJobs[0].jobId,
          done: currentDone,
          failed: currentFailed,
          total,
          status: anyRunning ? "RUNNING" : "DONE"
        });

        if (!anyRunning) {
          cumulativeDone = currentDone;
          cumulativeFailed = currentFailed;
          allFinished = true;
        } else {
          await new Promise(resolve => setTimeout(resolve, 10000));
        }
      }

      toast.success(`Generated ${cumulativeDone} offer letters successfully.`);
      if (cumulativeFailed > 0) toast.error(`${cumulativeFailed} failed to generate.`);

      setSelectedIds([]);
      setIsGenerating(false);
      setGenProgress(null);
      if (selectedRound) fetchApplications(selectedRound);

    } catch (err) {
      console.error("Generation failed", err);
      toast.error("An error occurred during generation.");
      setIsGenerating(false);
      setGenProgress(null);
    }
  };

  const handleSendOffer = () => {
    if (!selectedIds || selectedIds.length === 0) {
      toast.error("Please select at least one application to send offers to.");
      return;
    }
    setShowConfirmModal(true);
  };

  const executeSendOffer = async () => {
    setShowConfirmModal(false);
    setIsSending(true);
    setSendProgress(null);

    try {
      const rowKeys = [...selectedIds];
      const total = rowKeys.length;

      // Batching to avoid 400 URL length errors (approx 40 candidates per batch)
      const BATCH_SIZE = 40;
      const batchJobs: { jobId: string, size: number }[] = [];
      let initialFailed = 0;

      // 1. Submit ALL batches immediately to backend so they run in background
      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(k => k.split("|")[0]).join(";");
        const batchProgs = batch.map(k => k.split("|")[1]).join(";");

        const args = `application_id:${batchAppIds},` +
          `offered_programme:${batchProgs},` +
          `notify:${getConfig().ENABLE_EMAIL_NOTIFICATION},` +
          `is_online:${selectRoundType === "Online"}`;

        const url = `${apiConfig.getResourceUrl("applications")}?queryId=SEND_OFFER_LETTER&args=${encodeURIComponent(args)}`;

        try {
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();

          const jobId = res?.resource?.[0]?.extra_data?.job_id;
          if (res?.errCode === 0 && jobId) {
            batchJobs.push({ jobId, size: batch.length });
          } else {
            initialFailed += batch.length;
          }
        } catch (err) {
          initialFailed += batch.length;
        }
      }

      if (batchJobs.length === 0) {
        toast.error("Failed to start sending offers for any candidates.");
        setIsSending(false);
        return;
      }

      // 2. Poll all active jobs every 10 seconds
      let allFinished = false;
      let cumulativeDone = 0;
      let cumulativeFailed = initialFailed;

      while (!allFinished) {
        let currentDone = 0;
        let currentFailed = initialFailed;
        let anyRunning = false;

        for (const job of batchJobs) {
          try {
            const statusUrl = `${apiConfig.getResourceUrl("applications")}?queryId=GET_JOB_STATUS&args=${encodeURIComponent(`job_id:${job.jobId}`)}`;
            const statusRes = await apiFetch(statusUrl, { method: "GET" });
            const statusData = await statusRes.json();
            const jobData = statusData?.resource?.[0]?.extra_data;

            if (!jobData || jobData.status === "NOT_FOUND") {
              currentFailed += job.size;
              continue;
            }

            currentDone += (jobData.done || 0);
            currentFailed += (jobData.failed || 0);

            if (jobData.status === "RUNNING") {
              anyRunning = true;
            }
          } catch (err) {
            anyRunning = true; // network error, assume running
          }
        }

        setSendProgress({
          jobId: batchJobs[0].jobId,
          done: currentDone,
          failed: currentFailed,
          total,
          status: anyRunning ? "RUNNING" : "DONE"
        });

        if (!anyRunning) {
          cumulativeDone = currentDone;
          cumulativeFailed = currentFailed;
          allFinished = true;
        } else {
          await new Promise(resolve => setTimeout(resolve, 10000));
        }
      }

      toast.success(`Sent ${cumulativeDone} offers successfully.`);
      if (cumulativeFailed > 0) toast.error(`${cumulativeFailed} failed to send.`);

      setSelectedIds([]);
      if (selectedRound) fetchApplications(selectedRound);

    } catch (err) {
      console.error("Sending offers failed", err);
      toast.error("An error occurred while sending offers.");
    } finally {
      setIsSending(false);
      setSendProgress(null);
    }
  };

  const handleSendEmail = () => {
    setSendEmailOpen(true)
    const emails = selectedIds.map(idString => {
      const appId = idString.split("|")[0];
      const app = applications.find(a => a.id === appId);
      return app?.email;
    }).filter(Boolean);

    setSelectedEmails(emails as string[]);
  }

  const executeMarkUpgradeSent = async () => {
    if (activeTab !== "UPGRADED" || selectedIds.length === 0) return;
    try {
      const roundObj = rounds.find((r) => r.id === selectedRound);
      const appIds = selectedIds.map(k => k.split("|")[0]).join(";");
      const progs = selectedIds.map(k => k.split("|")[1] || "").join(";");

      const args = `application_id:${appIds},` +
        `offered_programme:${progs},` +
        `offered_round_id:${selectedRound},` +
        `offered_round_name:${roundObj?.name || ""},` +
        `is_online:${selectRoundType === "Online"}`;

      const url = `${apiConfig.getResourceUrl("applications")}?queryId=MARK_UPGRADE_SENT&args=${encodeURIComponent(args)}`;
      const response = await apiFetch(url, {
        method: "GET",
      });
      const data = await response.json();
      if (data.errCode === 0) {
        toast.success("Candidates marked as Upgrade Sent successfully!");
        if (selectedRound) fetchApplications(selectedRound); // refresh
      }
    } catch (err) {
      console.error(err);
    }
  };

  const executeMarkMtechPaidOfferSent = async () => {
    if (activeTab !== "UPGRADED" || selectedIds.length === 0) return;
    try {
      const roundObj = rounds.find((r) => r.id === selectedRound);
      const appIds = selectedIds.map(k => k.split("|")[0]).join(";");
      const progs = selectedIds.map(k => k.split("|")[1] || "").join(";");

      const args = `application_id:${appIds},` +
        `offered_programme:${progs},` +
        `offered_round_id:${selectedRound},` +
        `offered_round_name:${roundObj?.name || ""},` +
        `is_online:${selectRoundType === "Online"}`;

      const url = `${apiConfig.getResourceUrl("applications")}?queryId=MARK_MTECH_PAID_OFFER_SENT&args=${encodeURIComponent(args)}`;
      const response = await apiFetch(url, {
        method: "GET",
      });
      const data = await response.json();
      if (data.errCode === 0) {
        toast.success("Candidates marked as Offer Sent successfully!");
        if (selectedRound) fetchApplications(selectedRound); // refresh
      }
    } catch (err) {
      console.error(err);
    }
  };


  const handleMarkOnlineEligible = () => {
    if (selectedIds.length === 0) return;
    setShowEligibleConfirmModal(true);
  };

  const executeMarkOnlineEligible = async () => {
    setIsMarkingEligible(true);
    setShowEligibleConfirmModal(false);
    try {
      const BATCH_SIZE = 30;
      let successCount = 0;
      let failedCount = 0;
      const rowKeys = [...selectedIds];

      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(id => id.split("|")[0]).join(";");
        const batchProgs = batch.map(id => id.split("|")[1] || "").join(";");
        const hasProgs = batchProgs.replace(/;/g, "").trim().length > 0;
        const args = hasProgs
          ? `application_id:${batchAppIds},round_id:${selectedRound},offered_programme:${batchProgs}`
          : `application_id:${batchAppIds},round_id:${selectedRound}`;
        const url = `${apiConfig.getResourceUrl("applications")}?queryId=MARK_ONLINE_ELIGIBLE&args=${encodeURIComponent(args)}`;

        try {
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();
          if (res?.errCode === 0) {
            successCount += batch.length;
          } else {
            failedCount += batch.length;
          }
        } catch (err) {
          failedCount += batch.length;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully marked ${successCount} candidate(s) as online eligible & notified.`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to mark ${failedCount} candidate(s).`);
      }

      setSelectedIds([]);
      fetchApplications(selectedRound);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred.");
    } finally {
      setIsMarkingEligible(false);
    }
  };

  const handleGenerateTokens = () => {
    if (selectedIds.length === 0) return;
    setShowTokenConfirmModal(true);
  };

  const executeGenerateTokens = async () => {
    setIsGeneratingTokens(true);
    setShowTokenConfirmModal(false);
    try {
      const BATCH_SIZE = 30;
      let successCount = 0;
      let failedCount = 0;
      const rowKeys = [...selectedIds];

      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(id => id.split("|")[0]).join(";");
        const args = `application_id:${batchAppIds},round_id:${selectedRound}`;
        const url = `${apiConfig.getResourceUrl("applications")}?queryId=GENERATE_ONLINE_TOKENS&args=${encodeURIComponent(args)}`;

        try {
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();
          if (res?.errCode === 0) {
            successCount += batch.length;
          } else {
            failedCount += batch.length;
          }
        } catch (err) {
          failedCount += batch.length;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully generated tokens for ${successCount} candidate(s) & notified.`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to generate tokens for ${failedCount} candidate(s).`);
      }

      setSelectedIds([]);
      fetchApplications(selectedRound);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred.");
    } finally {
      setIsGeneratingTokens(false);
    }
  };

  const handleSendTokenEmail = () => {
    if (selectedIds.length === 0) return;
    setShowTokenEmailConfirmModal(true);
  };

  const executeSendTokenEmail = async () => {
    setIsSendingTokenEmail(true);
    setShowTokenEmailConfirmModal(false);
    try {
      const BATCH_SIZE = 30;
      let successCount = 0;
      let failedCount = 0;
      const rowKeys = [...selectedIds];

      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(id => id.split("|")[0]).join(";");
        const args = `application_id:${batchAppIds},round_id:${selectedRound}`;
        const url = `${apiConfig.getResourceUrl("applications")}?queryId=SEND_TOKEN_EMAIL&args=${encodeURIComponent(args)}`;
        try {
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();
          if (res?.errCode === 0) successCount += batch.length;
          else failedCount += batch.length;
        } catch (err) {
          failedCount += batch.length;
        }
      }

      if (successCount > 0) toast.success(`Successfully sent token emails to ${successCount} candidate(s).`);
      if (failedCount > 0) toast.error(`Failed to send token emails for ${failedCount} candidate(s).`);

      setSelectedIds([]);
      fetchApplications(selectedRound);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while sending token emails.");
    } finally {
      setIsSendingTokenEmail(false);
    }
  };




  const handleViewOnlineRound = async () => {
    const appId = await fetchApplicationByIIR(1);

    if (appId) {
      navigate(`/onlineRound/${appId}`);
      console.log("Application ID:", appId);
    }
  }

  const handleViewSeatMatrix = async () => {
    const appId = await fetchApplicationByIIR(1);

    if (appId) {
      if (isMtechRound) {
        navigate(`/mtech-online-display/${appId}`);
      } else {
        navigate(`/btech-online-display/${appId}`);
      }
      console.log("Application ID:", appId);
    } else {
      toast.error("Tokens not generated yet.");
    }
  }

  const handleCancelOffer = () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one offer to cancel.");
      return;
    }
    setCancelReason("");
    setShowCancelModal(true);
  };

  const executeCancelOffer = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation.");
      return;
    }

    setIsCancelling(true);
    setShowCancelModal(false);

    try {
      const BATCH_SIZE = 30;
      let successCount = 0;
      let failedCount = 0;

      const rowKeys = [...selectedIds];

      for (let i = 0; i < rowKeys.length; i += BATCH_SIZE) {
        const batch = rowKeys.slice(i, i + BATCH_SIZE);
        const batchAppIds = batch.map(k => k.split("|")[0]).join(";");
        const batchProgs = batch.map(k => k.split("|")[1]).join(";");

        try {
          const encodedReason = btoa(unescape(encodeURIComponent(cancelReason)));
          const args = `application_id:${batchAppIds},offered_programme:${batchProgs},reason:${encodedReason},is_online:${selectRoundType === "Online"}`;
          const url = `${apiConfig.getResourceUrl("applications")}?queryId=CANCEL_OFFER&args=${encodeURIComponent(args)}`;
          const response = await apiFetch(url, { method: "GET" });
          const res = await response.json();

          if (res?.errCode === 0) {
            const results = res.resource || [];
            successCount += results.length;
          } else {
            failedCount += batch.length;
          }
        } catch (err) {
          failedCount += batch.length;
        }
        await new Promise((res) => setTimeout(res, 200));
      }

      toast.success(`Cancelled ${successCount} offers successfully.`);
      if (failedCount > 0) {
        toast.error(`${failedCount} offers failed to cancel.`);
      }

      setSelectedIds([]);
      if (selectedRound) fetchApplications(selectedRound);

    } catch (err) {
      console.error("Cancelling offers failed", err);
      toast.error("An error occurred while cancelling offers.");
    } finally {
      setIsCancelling(false);
    }
  };



  const getOnlineField = (app: any, fieldName: 'onlineRound' | 'online_round_status' | 'online_offer_status' | 'online_sliding_status' | 'online_offered_payment_status' | 'online_upgrade_notification_sent' | 'online_round_token' | 'online_round_allocated_programme' | 'online_round_allocated_programme_2' | 'online_round_token_2' | 'online_offer_status_2' | 'online_round_offer_letter_doc_id_2' | 'online_round_offer_letter_doc_id') => {
    if (!isMtechRound) {
      if (isOnlineRound5) {
        if (fieldName === 'onlineRound') return app.online_round5_id;
        if (fieldName === 'online_round_status') return app.online_round5_status;
        if (fieldName === 'online_offer_status') return app.online_round5_offer_status;
        if (fieldName === 'online_sliding_status') return app.online_round5_sliding_status;
        if (fieldName === 'online_round_token') return app.online_round5_token;
        if (fieldName === 'online_round_allocated_programme') return app.online_round5_allocated_programme;
        if (fieldName === 'online_offered_payment_status') return app.online_round5_offered_payment_status;
        if (fieldName === 'online_upgrade_notification_sent') return app.online_round5_upgrade_notification_sent;
        if (fieldName === 'online_round_allocated_programme_2') return app.online_round5_allocated_programme_2;
        if (fieldName === 'online_round_token_2') return app.online_round5_token_2;
        if (fieldName === 'online_offer_status_2') return app.online_round5_offer_status_2;
        if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round5_offer_letter_doc_id_2;
        if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round5_offer_letter_doc_id;
      }
      if (isOnlineRound4) {
        if (fieldName === 'onlineRound') return app.online_round4_id;
        if (fieldName === 'online_round_status') return app.online_round4_status;
        if (fieldName === 'online_offer_status') return app.online_round4_offer_status;
        if (fieldName === 'online_sliding_status') return app.online_round4_sliding_status;
        if (fieldName === 'online_round_token') return app.online_round4_token;
        if (fieldName === 'online_round_allocated_programme') return app.online_round4_allocated_programme;
        if (fieldName === 'online_offered_payment_status') return app.online_round4_offered_payment_status;
        if (fieldName === 'online_upgrade_notification_sent') return app.online_round4_upgrade_notification_sent;
        if (fieldName === 'online_round_allocated_programme_2') return app.online_round4_allocated_programme_2;
        if (fieldName === 'online_round_token_2') return app.online_round4_token_2;
        if (fieldName === 'online_offer_status_2') return app.online_round4_offer_status_2;
        if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round4_offer_letter_doc_id_2;
        if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round4_offer_letter_doc_id;
      }
      if (isOnlineRound3) {
        if (fieldName === 'onlineRound') return app.online_round3_id;
        if (fieldName === 'online_round_status') return app.online_round3_status;
        if (fieldName === 'online_offer_status') return app.online_round3_offer_status;
        if (fieldName === 'online_sliding_status') return app.online_round3_sliding_status;
        if (fieldName === 'online_round_token') return app.online_round3_token;
        if (fieldName === 'online_round_allocated_programme') return app.online_round3_allocated_programme;
        if (fieldName === 'online_offered_payment_status') return app.online_round3_offered_payment_status;
        if (fieldName === 'online_upgrade_notification_sent') return app.online_round3_upgrade_notification_sent;
        if (fieldName === 'online_round_allocated_programme_2') return app.online_round3_allocated_programme_2;
        if (fieldName === 'online_round_token_2') return app.online_round3_token_2;
        if (fieldName === 'online_offer_status_2') return app.online_round3_offer_status_2;
        if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round3_offer_letter_doc_id_2;
        if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round3_offer_letter_doc_id;
      }
      if (isOnlineRound2) {
        if (fieldName === 'onlineRound') return app.online_round2_id;
        if (fieldName === 'online_round_status') return app.online_round2_status;
        if (fieldName === 'online_offer_status') return app.online_round2_offer_status;
        if (fieldName === 'online_sliding_status') return app.online_round2_sliding_status;
        if (fieldName === 'online_round_token') return app.online_round2_token;
        if (fieldName === 'online_round_allocated_programme') return app.online_round2_allocated_programme;
        if (fieldName === 'online_offered_payment_status') return app.online_round2_offered_payment_status;
        if (fieldName === 'online_upgrade_notification_sent') return app.online_round2_upgrade_notification_sent;
        if (fieldName === 'online_round_allocated_programme_2') return app.online_round2_allocated_programme_2;
        if (fieldName === 'online_round_token_2') return app.online_round2_token_2;
        if (fieldName === 'online_offer_status_2') return app.online_round2_offer_status_2;
        if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round2_offer_letter_doc_id_2;
        if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round2_offer_letter_doc_id;
      }
      return app[fieldName];
    }

    const matched = app.extra_data?.matched_programme;
    const normalize = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    
    let isSecondary = false;
    let isPrimary = false;
    
    if (matched) {
      if (isOnlineRound5) {
        if (normalize(app.online_round5_allocated_programme_2) === normalize(matched)) isSecondary = true;
        if (normalize(app.online_round5_allocated_programme) === normalize(matched)) isPrimary = true;
      } else if (isOnlineRound4) {
        if (normalize(app.online_round4_allocated_programme_2) === normalize(matched)) isSecondary = true;
        if (normalize(app.online_round4_allocated_programme) === normalize(matched)) isPrimary = true;
      } else if (isOnlineRound3) {
        if (normalize(app.online_round3_allocated_programme_2) === normalize(matched)) isSecondary = true;
        if (normalize(app.online_round3_allocated_programme) === normalize(matched)) isPrimary = true;
      } else if (isOnlineRound2) {
        if (normalize(app.online_round2_allocated_programme_2) === normalize(matched)) isSecondary = true;
        if (normalize(app.online_round2_allocated_programme) === normalize(matched)) isPrimary = true;
      } else {
        if (normalize(app.online_round_allocated_programme_2) === normalize(matched)) isSecondary = true;
        if (normalize(app.online_round_allocated_programme) === normalize(matched)) isPrimary = true;
      }
    }

    if (fieldName === 'online_offer_status') {
      if (isSecondary) return isOnlineRound5 ? app.online_round5_offer_status_2 : (isOnlineRound4 ? app.online_round4_offer_status_2 : (isOnlineRound3 ? app.online_round3_offer_status_2 : (isOnlineRound2 ? app.online_round2_offer_status_2 : app.online_offer_status_2)));
      if (isPrimary || !matched) return isOnlineRound5 ? app.online_round5_offer_status : (isOnlineRound4 ? app.online_round4_offer_status : (isOnlineRound3 ? app.online_round3_offer_status : (isOnlineRound2 ? app.online_round2_offer_status : app.online_offer_status)));
      return null;
    }
    if (fieldName === 'online_round_token') {
      if (isSecondary) return isOnlineRound5 ? app.online_round5_token_2 : (isOnlineRound4 ? app.online_round4_token_2 : (isOnlineRound3 ? app.online_round3_token_2 : (isOnlineRound2 ? app.online_round2_token_2 : app.online_round_token_2)));
      if (isPrimary || !matched) return isOnlineRound5 ? app.online_round5_token : (isOnlineRound4 ? app.online_round4_token : (isOnlineRound3 ? app.online_round3_token : (isOnlineRound2 ? app.online_round2_token : app.online_round_token)));
      return null;
    }
    if (fieldName === 'online_round_offer_letter_doc_id') {
      if (isSecondary) return isOnlineRound5 ? app.online_round5_offer_letter_doc_id_2 : (isOnlineRound4 ? app.online_round4_offer_letter_doc_id_2 : (isOnlineRound3 ? app.online_round3_offer_letter_doc_id_2 : (isOnlineRound2 ? app.online_round2_offer_letter_doc_id_2 : app.online_round_offer_letter_doc_id_2)));
      if (isPrimary || !matched) return isOnlineRound5 ? app.online_round5_offer_letter_doc_id : (isOnlineRound4 ? app.online_round4_offer_letter_doc_id : (isOnlineRound3 ? app.online_round3_offer_letter_doc_id : (isOnlineRound2 ? app.online_round2_offer_letter_doc_id : app.online_round_offer_letter_doc_id)));
      return null;
    }

    if (isOnlineRound3) {
      if (fieldName === 'onlineRound') return app.online_round3_id;
      if (fieldName === 'online_round_status') return app.online_round3_status;
      if (fieldName === 'online_sliding_status') return app.online_round3_sliding_status;
      if (fieldName === 'online_round_allocated_programme') return app.online_round3_allocated_programme;
      if (fieldName === 'online_offered_payment_status') return app.online_round3_offered_payment_status;
      if (fieldName === 'online_upgrade_notification_sent') return app.online_round3_upgrade_notification_sent;
    } else if (isOnlineRound2) {
      if (fieldName === 'onlineRound') return app.online_round2_id;
      if (fieldName === 'online_round_status') return app.online_round2_status;
      if (fieldName === 'online_sliding_status') return app.online_round2_sliding_status;
      if (fieldName === 'online_round_allocated_programme') return app.online_round2_allocated_programme;
      if (fieldName === 'online_offered_payment_status') return app.online_round2_offered_payment_status;
      if (fieldName === 'online_upgrade_notification_sent') return app.online_round2_upgrade_notification_sent;
    } else {
      return app[fieldName];
    }
    return null;
  };

  const getEffectiveStatus = (app: Application) => {
    if (selectRoundType === "Online") {
      if (isOnlineRound5) {
        return app.online_round5_offer_status || "NONE";
      }
      if (isOnlineRound4) {
        return app.online_round4_offer_status || "NONE";
      }
      if (isOnlineRound3) {
        return app.online_round3_offer_status || "NONE";
      }
      if (isOnlineRound2) {
        return app.online_round2_offer_status || "NONE";
      }
      return app.online_offer_status || "NONE";
    }

    const matched = app.extra_data?.matched_programme;
    const normalize = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

    // 1. If we are viewing the round that matches their LATEST state
    if (app.offered_round_id === selectedRound) {
      if (matched) {
        if (app.offered_programme_1 === matched && app.offer_status_1 && app.offer_status_1 !== "NONE") return app.offer_status_1;
        if (app.offered_programme_2 === matched && app.offer_status_2 && app.offer_status_2 !== "NONE") return app.offer_status_2;
        if (app.offered_programme_3 === matched && app.offer_status_3 && app.offer_status_3 !== "NONE") return app.offer_status_3;

        const normMatched = normalize(matched);
        if (normalize(app.offered_programme_1) === normMatched && app.offer_status_1 && app.offer_status_1 !== "NONE") return app.offer_status_1;
        if (normalize(app.offered_programme_2) === normMatched && app.offer_status_2 && app.offer_status_2 !== "NONE") return app.offer_status_2;
        if (normalize(app.offered_programme_3) === normMatched && app.offer_status_3 && app.offer_status_3 !== "NONE") return app.offer_status_3;
      }
      return app.offer_status || "NONE";
    }

    // 2. If we are viewing a PAST round that matches their PREVIOUS state
    if (app.prev_offered_round_id === selectedRound) {
      if (matched && isMtechRound) {
        if (app.offered_programme_1 === matched && app.offer_status_1 && app.offer_status_1 !== "NONE") return app.offer_status_1;
        if (app.offered_programme_2 === matched && app.offer_status_2 && app.offer_status_2 !== "NONE") return app.offer_status_2;
        if (app.offered_programme_3 === matched && app.offer_status_3 && app.offer_status_3 !== "NONE") return app.offer_status_3;

        const normMatched = normalize(matched);
        if (normalize(app.offered_programme_1) === normMatched && app.offer_status_1 && app.offer_status_1 !== "NONE") return app.offer_status_1;
        if (normalize(app.offered_programme_2) === normMatched && app.offer_status_2 && app.offer_status_2 !== "NONE") return app.offer_status_2;
        if (normalize(app.offered_programme_3) === normMatched && app.offer_status_3 && app.offer_status_3 !== "NONE") return app.offer_status_3;
      }
      return app.prev_offer_status || "NONE";
    }

    // 3. Fallback for legacy applications missing round IDs
    if (!app.offered_round_id && !app.prev_offered_round_id) {
      if (app.offer_status && app.offer_status !== "NONE" && app.offer_status !== "DRAFT" && app.offer_status !== "SUBMITTED") {
        return app.offer_status;
      }
    }

    // 4. Candidate has no active/historical status matching THIS round
    return "NONE";
  };

  const partitionedApplications = useMemo(() => {
    let list = applications;

    if (isMtechRound) {
      list = list.filter(app => {
        const matched = app.extra_data?.matched_programme;
        if (!matched) return true;

        const normalizeProg = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
        const normMatched = normalizeProg(matched);

        // If the candidate's base application doesn't record THIS round yet,
        // it means they are newly allocated in the snapshot and unconfirmed.
        // The backend ONLY returned the snapshot's matched_programme, so it's accurate without duplicates.
        if (app.offered_round_id !== selectedRound && app.prev_offered_round_id !== selectedRound) {
          return true;
        }

        // If they have never been upgraded, they only have offers from their FIRST round.
        // There are no "previous" offers to filter out, so all active slots belong to this round.
        if (!app.prev_offered_round_id) {
          return true;
        }

        let oldProgs: string[] = [];
        let newProgs: string[] = [];

        if (app.prev_offered_programme) {
          oldProgs.push(normalizeProg(app.prev_offered_programme)!);
          if (app.offered_programme_1 && normalizeProg(app.offered_programme_1) !== oldProgs[0]) newProgs.push(normalizeProg(app.offered_programme_1)!);
          if (app.offered_programme_2 && normalizeProg(app.offered_programme_2) !== oldProgs[0]) newProgs.push(normalizeProg(app.offered_programme_2)!);
          if (app.offered_programme_3 && normalizeProg(app.offered_programme_3) !== oldProgs[0]) newProgs.push(normalizeProg(app.offered_programme_3)!);
        } else {
          let slots: any[] = [];
          if (selectRoundType === "Online") {
            if (isOnlineRound5) {
              slots = [
                { p: app.online_round5_allocated_programme, s: app.online_round5_offer_status, d: app.online_round5_offer_letter_doc_id },
                { p: app.online_round5_allocated_programme_2, s: app.online_round5_offer_status_2, d: app.online_round5_offer_letter_doc_id_2 }
              ].filter(x => x.p);
            } else if (isOnlineRound4) {
              slots = [
                { p: app.online_round4_allocated_programme, s: app.online_round4_offer_status, d: app.online_round4_offer_letter_doc_id },
                { p: app.online_round4_allocated_programme_2, s: app.online_round4_offer_status_2, d: app.online_round4_offer_letter_doc_id_2 }
              ].filter(x => x.p);
            } else if (isOnlineRound3) {
              slots = [
                { p: app.online_round3_allocated_programme, s: app.online_round3_offer_status, d: app.online_round3_offer_letter_doc_id },
                { p: app.online_round3_allocated_programme_2, s: app.online_round3_offer_status_2, d: app.online_round3_offer_letter_doc_id_2 }
              ].filter(x => x.p);
            } else if (isOnlineRound2) {
              slots = [
                { p: app.online_round2_allocated_programme, s: app.online_round2_offer_status, d: app.online_round2_offer_letter_doc_id },
                { p: app.online_round2_allocated_programme_2, s: app.online_round2_offer_status_2, d: app.online_round2_offer_letter_doc_id_2 }
              ].filter(x => x.p);
            } else {
              slots = [
                { p: app.online_round_allocated_programme, s: app.online_offer_status, d: app.online_round_offer_letter_doc_id },
                { p: app.online_round_allocated_programme_2, s: app.online_offer_status_2, d: app.online_round_offer_letter_doc_id_2 }
              ].filter(x => x.p);
            }
          } else {
            slots = [
              { p: app.offered_programme_1, s: app.offer_status_1, d: app.offer_letter_doc_id_1 },
              { p: app.offered_programme_2, s: app.offer_status_2, d: app.offer_letter_doc_id_2 },
              { p: app.offered_programme_3, s: app.offer_status_3, d: app.offer_letter_doc_id_3 }
            ].filter(x => x.p);
          }

          const globalDocSlot = slots.find(x => x.d && x.d === app.offer_letter_doc_id);
          const activeProcessingSlot = slots.find(x => x.s === "OFFER_CONFIRMED" || x.s === "OFFER_GENERATED");
          const presumedNew = activeProcessingSlot || globalDocSlot || slots[slots.length - 1];

          if (selectRoundType === "Online") {
            const activeStatuses = ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED"];
            const activeSlots = slots.filter(x => x.s && activeStatuses.includes(x.s));
            
            if (activeSlots.length > 0) {
              slots.forEach(x => {
                if (activeSlots.includes(x)) newProgs.push(normalizeProg(x.p)!);
                else oldProgs.push(normalizeProg(x.p)!);
              });
            } else {
              slots.forEach(x => {
                if (x === presumedNew) newProgs.push(normalizeProg(x.p)!);
                else oldProgs.push(normalizeProg(x.p)!);
              });
            }
          } else {
            slots.forEach(x => {
              if (x === presumedNew) newProgs.push(normalizeProg(x.p)!);
              else oldProgs.push(normalizeProg(x.p)!);
            });
          }
        }

        if (app.offered_round_id === selectedRound) {
          return newProgs.length > 0 ? newProgs.includes(normMatched!) : true;
        } else if (app.prev_offered_round_id === selectedRound) {
          return oldProgs.length > 0 ? oldProgs.includes(normMatched!) : true;
        }

        return true;
      });
    }

    return list;
  }, [applications, isMtechRound, selectedRound]);

  const filteredApplications = useMemo(() => {
    let list = partitionedApplications;

    if (selectRoundType === "Online") {
      if (activeTab === "ELIGIBLE") {
        list = list.filter(app => {
          return true;
        });
        // For MTech: sort by programme then by GATE score descending within each programme
        if (isMtechRound) {
          const progOrder: Record<string, number> = { MTech_CSE: 0, MTech_ECE: 1, MTech_AI_DS: 2 };
          const normP = (p?: string) => {
            if (!p) return "";
            if (p.toLowerCase().replace(/[^a-z]/g, "").includes("cse")) return "MTech_CSE";
            if (p.toLowerCase().replace(/[^a-z]/g, "").includes("ece")) return "MTech_ECE";
            return "MTech_AI_DS";
          };
          list = [...list].sort((a, b) => {
            const pa = normP(a.extra_data?.matched_programme);
            const pb = normP(b.extra_data?.matched_programme);
            const oa = progOrder[pa] ?? 99;
            const ob = progOrder[pb] ?? 99;
            if (oa !== ob) return oa - ob;
            // within same programme: sort by matched_score descending
            const sa = Number(a.extra_data?.matched_score) || 0;
            const sb = Number(b.extra_data?.matched_score) || 0;
            return sb - sa;
          });
        }
      } else if (activeTab === "REGISTERED") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && (["ONLINE_REGISTERED", "TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || "") || !!getOnlineField(app, 'online_offer_status')));
      } else if (activeTab === "TOKEN_EMAIL_SEND") {
        // Two-step token tab: candidates with token generated (email pending or sent)
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || ""));
      } else if (activeTab === "POOL") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && (["TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || "") || !!getOnlineField(app, 'online_offer_status')));
      } else if (activeTab === "PENDING_GEN") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') !== "PAID_SLIDE");
      } else if (activeTab === "UPGRADED") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE" && getOnlineField(app, 'online_upgrade_notification_sent') !== "YES");
      } else if (activeTab === "PENDING_SEND") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') !== "PAID_SLIDE");
      } else if (activeTab === "SENT") {
        list = list.filter(app => {
          if (!(getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound'))) return false;
          if (!["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"].includes(getOnlineField(app, 'online_offer_status') || "")) return false;
          if (getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE" && getOnlineField(app, 'online_upgrade_notification_sent') !== "YES") return false;

          if (statusFilter !== "ALL" && getOnlineField(app, 'online_offer_status') !== statusFilter) {
            return false;
          }
          if (
            offeredPaymentStatusFilter !== "ALL" &&
            getOnlineField(app, 'online_offered_payment_status') !== offeredPaymentStatusFilter
          ) {
            return false;
          }

          return true;
        });
      } else if (activeTab === "CANCELLED") {
        list = list.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && getOnlineField(app, 'online_offer_status') === "OFFER_CANCELLED");
      } else {
        list = [];
      }
    } else {
      // Only POOL tab shows everyone (with non-pool entries disabled).
      // Other tabs filter the list strictly to their specific workflow stage OR beyond.
      if (activeTab !== "POOL") {
        list = list.filter(app => {
          const status = getEffectiveStatus(app);
          const activeOfferedPaymentStatus = app.offered_payment_status;
          const allPostGen = ["OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "EXPIRED"];
          const allPostSend = ["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "EXPIRED"];

          if (activeTab === "PENDING_GEN") {
            return status === "OFFER_CONFIRMED" && activeOfferedPaymentStatus !== "SUCCESS";
          }
          if (activeTab === "UPGRADED") {
            if (isBtechRound && isSlidingRound) {
              return (app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") &&
                app.offered_payment_status === "SUCCESS" &&
                app.upgrade_notification_sent !== "YES" &&
                app.offered_payment_round !== selectedRound;
            }
            if (isMtechRound && !isFirstRound) {
              return app.offered_payment_status === "SUCCESS" && app.upgrade_notification_sent !== "YES" && app.offered_payment_round !== selectedRound;
            }
            return false;
          }
          if (activeTab === "PENDING_SEND") {
            return status === "OFFER_GENERATED" || allPostSend.includes(status);
          }
          if (activeTab === "SENT") {
            const activeStatuses = ["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"];

            if (!activeStatuses.includes(status)) return false;

            // Hide candidates who belong in the UPGRADED tab
            if (isBtechRound && isSlidingRound) {
              if (
                (app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") &&
                app.offered_payment_status === "SUCCESS" &&
                app.upgrade_notification_sent !== "YES" &&
                app.offered_payment_round !== selectedRound
              ) {
                return false;
              }
            }
            if (isMtechRound && !isFirstRound) {
              if (app.offered_payment_status === "SUCCESS" && app.upgrade_notification_sent !== "YES" && app.offered_payment_round !== selectedRound) {
                return false;
              }
            }

            if (statusFilter !== "ALL" && status !== statusFilter) {
              return false;
            }

            // FIXED
            if (
              offeredPaymentStatusFilter !== "ALL" &&
              offeredPaymentStatusFilter !== activeOfferedPaymentStatus
            ) {
              return false;
            }

            return true;
          }
          if (activeTab === "CANCELLED") return status === "OFFER_CANCELLED";
          return true;
        });
      } else {
        // POOL tab
        list = list.filter(app => {
          const status = getEffectiveStatus(app);
          // Exclude permanently dead statuses from POOL tab
          if (["REJECTED", "WITHDRAWN", "OFFER_CANCELLED", "EXPIRED"].includes(status)) {
            return false;
          }
          // The backend snapshot already controls exactly who appears in this round's pool.
          // Do NOT additionally filter by offered_round_id here — prev-round OFFER_SENT candidates
          // are valid candidates for new-round allocation and should appear in the Confirm Offer List.
          return true;
        });
      }
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      list = list.filter(app => {
        const appNo = String(app.application_number || "").toLowerCase();
        const name = String(app.fullname || "").toLowerCase();
        const email = String(app.email || "").toLowerCase();
        const extra = app.extra_data || {};
        const prog = String(extra.matched_programme || "").toLowerCase();
        return appNo.includes(searchLower) || name.includes(searchLower) || email.includes(searchLower) || prog.includes(searchLower);
      });
    }

    // Sort the list
    list = [...list].sort((a, b) => {
      if (selectRoundType === "Online") {
        if (activeTab === "ELIGIBLE" || activeTab === "REGISTERED" || activeTab === "TOKEN_EMAIL_SEND") {
          if (isBtechRound) {
            const bjrA = Number(a.bjr) || 999999;
            const bjrB = Number(b.bjr) || 999999;
            if (bjrA !== bjrB) return bjrA - bjrB;

            const jeeA = Number(a.jeemain_rank) || 999999;
            const jeeB = Number(b.jeemain_rank) || 999999;
            if (jeeA !== jeeB) return jeeA - jeeB;

            return String(a.application_number).localeCompare(String(b.application_number));
          } else if (isMtechRound) {
            const progOrder: Record<string, number> = { MTech_CSE: 0, MTech_ECE: 1, MTech_AI_DS: 2 };
            const normP = (p?: string) => {
              if (!p) return "";
              if (p.toLowerCase().replace(/[^a-z]/g, "").includes("cse")) return "MTech_CSE";
              if (p.toLowerCase().replace(/[^a-z]/g, "").includes("ece")) return "MTech_ECE";
              return "MTech_AI_DS";
            };
            const pa = normP(a.extra_data?.matched_programme || a.offered_programme);
            const pb = normP(b.extra_data?.matched_programme || b.offered_programme);
            const oa = progOrder[pa] ?? 99;
            const ob = progOrder[pb] ?? 99;
            if (oa !== ob) return oa - ob;

            const getScore = (app: Application) => {
              const matched = app.extra_data?.matched_score;
              if (matched !== undefined && Number(matched) !== 0) return Number(matched);
              let maxScore = 0;
              for (let i = 1; i <= 5; i++) {
                const s = Number(app[`pref${i}score` as keyof Application]);
                if (!isNaN(s) && s > maxScore) maxScore = s;
              }
              return maxScore;
            };
            const scoreA = getScore(a);
            const scoreB = getScore(b);
            if (scoreA !== scoreB) return scoreB - scoreA;

            // Tie-breaker using cgpa (graduation percentage)
            const cgpaA = Number(a.cgpa) || 0;
            const cgpaB = Number(b.cgpa) || 0;
            if (cgpaA !== cgpaB) return cgpaB - cgpaA;

            return String(a.application_number).localeCompare(String(b.application_number));
          }
        }
      }

      // Default sorting for other tabs and offline rounds
      if (isBtechRound) {
        const tokenA = getOnlineField(a, 'online_round_token');
        const tokenB = getOnlineField(b, 'online_round_token');
        const valA = selectRoundType === "Online" ? (tokenA || a.iir) : a.iir;
        const valB = selectRoundType === "Online" ? (tokenB || b.iir) : b.iir;
        const iirA = valA || 999999;
        const iirB = valB || 999999;
        if (iirA !== iirB) return Number(iirA) - Number(iirB);
        return String(a.application_number).localeCompare(String(b.application_number));
      } else {
        const getScore = (app: Application) => {
          if (app.extra_data?.matched_score !== undefined) return Number(app.extra_data.matched_score) || 0;
          return 0;
        };
        const scoreA = getScore(a);
        const scoreB = getScore(b);
        if (scoreA !== scoreB) return scoreB - scoreA;

        const cgpaA = Number(a.cgpa) || 0;
        const cgpaB = Number(b.cgpa) || 0;
        if (cgpaA !== cgpaB) return cgpaB - cgpaA;

        return String(a.application_number).localeCompare(String(b.application_number));
      }
    });

    return list;
  }, [partitionedApplications, searchText, activeTab, statusFilter, offeredPaymentStatusFilter, selectRoundType, selectedRound]);

  const hasTokensGenerated = useMemo(() => {
    return partitionedApplications.some(app => app.iir != null || getOnlineField(app, 'online_round_token') != null);
  }, [partitionedApplications]);



  // Determines if a row is "read-only" in the current tab.
  // A row is disabled if its programme-specific status does NOT match
  // what this tab is expecting as its actionable status.
  const isRowDisabled = (app: Application): boolean => {
    if (selectRoundType === "Online") {
      if (activeTab === "ELIGIBLE") return !!getOnlineField(app, 'online_round_status');
      if (activeTab === "REGISTERED") return getOnlineField(app, 'online_round_status') !== "ONLINE_REGISTERED";
      if (activeTab === "TOKEN_EMAIL_SEND") return getOnlineField(app, 'online_round_status') !== "TOKEN_GENERATED";
      if (activeTab === "POOL") return !!getOnlineField(app, 'online_offer_status') && getOnlineField(app, 'online_offer_status') !== "NONE";
      if (activeTab === "PENDING_GEN") return getOnlineField(app, 'online_offer_status') !== "OFFER_CONFIRMED" || getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE";
      if (activeTab === "UPGRADED") return getOnlineField(app, 'online_offer_status') !== "OFFER_CONFIRMED" || getOnlineField(app, 'online_sliding_status') !== "PAID_SLIDE";
      if (activeTab === "PENDING_SEND") return getOnlineField(app, 'online_offer_status') !== "OFFER_GENERATED" || getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE";
      if (activeTab === "SENT") return !["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"].includes(getOnlineField(app, 'online_offer_status') || "");
      if (activeTab === "CANCELLED") return getOnlineField(app, 'online_offer_status') !== "OFFER_CANCELLED";
      return true;
    }
    const status = getEffectiveStatus(app);
    const activeOfferedPaymentStatus = app.offered_payment_status;
    if (activeTab === "POOL") {
      if ((app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") && activeOfferedPaymentStatus === "SUCCESS") return true;
      return !(!status || status === "NONE" || status === "DRAFT" || status === "SUBMITTED" || status === "EDIT_SUBMIT");
    }
    if (activeTab === "PENDING_GEN") return status !== "OFFER_CONFIRMED" || activeOfferedPaymentStatus === "SUCCESS";
    if (activeTab === "UPGRADED") {
      if (isBtechRound && isSlidingRound) {
        return !((app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") && activeOfferedPaymentStatus === "SUCCESS" && app.offered_payment_round !== selectedRound);
      }
      if (isMtechRound && !isFirstRound) {
        return !(activeOfferedPaymentStatus === "SUCCESS" && app.offered_payment_round !== selectedRound);
      }
      return true;
    }
    if (activeTab === "PENDING_SEND") return status !== "OFFER_GENERATED";
    if (activeTab === "SENT") return !["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"].includes(status);
    if (activeTab === "CANCELLED") return status !== "OFFER_CANCELLED";
    return false;
  };


  const totalCount = filteredApplications.length;

  const tabCounts = useMemo(() => {
    if (selectRoundType === "Online") {
      return {
        ELIGIBLE: partitionedApplications.length,
        REGISTERED: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && (["ONLINE_REGISTERED", "TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || "") || !!getOnlineField(app, 'online_offer_status'))).length,
        TOKEN_EMAIL_SEND: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || "")).length,
        POOL: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && (["TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(app, 'online_round_status') || "") || !!getOnlineField(app, 'online_offer_status'))).length,
        PENDING_GEN: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') !== "PAID_SLIDE").length,
        UPGRADED: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE" && getOnlineField(app, 'online_upgrade_notification_sent') !== "YES").length,
        PENDING_SEND: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && ["OFFER_GENERATED", "OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN", "OFFER_CANCELLED"].includes(getOnlineField(app, 'online_offer_status') || "") && getOnlineField(app, 'online_sliding_status') !== "PAID_SLIDE").length,
        SENT: partitionedApplications.filter(app => {
          if (!(getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound'))) return false;
          if (!["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"].includes(getOnlineField(app, 'online_offer_status') || "")) return false;
          if (getOnlineField(app, 'online_sliding_status') === "PAID_SLIDE" && getOnlineField(app, 'online_upgrade_notification_sent') !== "YES") return false;
          return true;
        }).length,
        CANCELLED: partitionedApplications.filter(app => (getOnlineField(app, 'onlineRound') === selectedRound || !getOnlineField(app, 'onlineRound')) && getOnlineField(app, 'online_offer_status') === "OFFER_CANCELLED").length,
      };
    }
    return {
      ELIGIBLE: 0,
      REGISTERED: 0,
      POOL: partitionedApplications.filter(app => {
        const s = getEffectiveStatus(app);
        return !s || s === "NONE" || s === "DRAFT" || s === "SUBMITTED" || s === "EDIT_SUBMIT";
      }).length,
      PENDING_GEN: partitionedApplications.filter(app => {
        const s = getEffectiveStatus(app);
        return s === "OFFER_CONFIRMED" && app.offered_payment_status !== "SUCCESS";
      }).length,
      UPGRADED: partitionedApplications.filter(app => {
        if (isBtechRound && isSlidingRound) {
          return (app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") &&
            app.offered_payment_status === "SUCCESS" &&
            app.upgrade_notification_sent !== "YES" &&
            app.offered_payment_round !== selectedRound;
        }
        if (isMtechRound && !isFirstRound) {
          return app.offered_payment_status === "SUCCESS" && app.upgrade_notification_sent !== "YES" && app.offered_payment_round !== selectedRound;
        }
        return false;
      }).length,
      PENDING_SEND: partitionedApplications.filter(app => {
        const s = getEffectiveStatus(app);
        return s === "OFFER_GENERATED";
      }).length,
      SENT: partitionedApplications.filter(app => {
        const s = getEffectiveStatus(app);
        const activeStatuses = ["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"];
        if (!activeStatuses.includes(s)) return false;
        if (isBtechRound && isSlidingRound) {
          if ((app.current_sliding_status === "SLIDE" || app.current_sliding_status === "PAID_SLIDE") &&
            app.offered_payment_status === "SUCCESS" &&
            app.upgrade_notification_sent !== "YES" &&
            app.offered_payment_round !== selectedRound) {
            return false;
          }
        }
        if (isMtechRound && !isFirstRound) {
          if (app.offered_payment_status === "SUCCESS" && app.upgrade_notification_sent !== "YES" && app.offered_payment_round !== selectedRound) {
            return false;
          }
        }
        return true;
      }).length,
      CANCELLED: partitionedApplications.filter(app => {
        const s = getEffectiveStatus(app);
        return s === "OFFER_CANCELLED";
      }).length,
    };
  }, [partitionedApplications, isSlidingRound, isMtechRound, selectRoundType, selectedRound]);

  const handleExport = () => {
    if (!filteredApplications.length) {
      toast.error("No data to export");
      return;
    }

    const normalize = (str?: string) =>
      str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

    const getPrefIndex = (app: Application) => {
      const matched = app.extra_data?.matched_programme;
      if (!matched) return null;
      if (normalize(matched) === normalize(app.pref1)) return 1;
      if (normalize(matched) === normalize(app.pref2)) return 2;
      if (normalize(matched) === normalize(app.pref3)) return 3;
      return null;
    };

    const wb = XLSX.utils.book_new();

    if (isBtechRound && selectRoundType === "Online") {
      // B.Tech Online: only columns shown in the online table
      const headerRow = [
        "S.No.", "Application No", "Name", "Email", "Programme",
        "JEE Main Rank (CRL)", "JEE Main Score", "JEE Advanced Rank (CRL)", "JEE Advanced Score",
        "Best JEE Rank (BJR)", "Rank Token Number", "Sliding Status",
        "Offer Status", "Online Round Eligibility",
      ];

      const dataRows = filteredApplications.map((app: Application) => {
        const extra = app.extra_data || {};
        const onlineAllocatedProg = getOnlineField(app, 'online_round_allocated_programme');
        const matchedProgramme = onlineAllocatedProg
          ? onlineAllocatedProg.replace(/_/g, " ")
          : "—";
        const status = getEffectiveStatus(app);
        const onlineEligibility = getOnlineField(app, 'online_round_status')?.replace(/_/g, " ") || "NOT INVITED";

        return [
          0,
          app.application_number || "",
          app.fullname || "",
          app.email || "",
          matchedProgramme,
          (app.jeemain_rank !== null && app.jeemain_rank !== undefined && app.jeemain_rank !== "") ? app.jeemain_rank : "N/A",
          (app.jeemain_score !== null && app.jeemain_score !== undefined && app.jeemain_score !== "") ? app.jeemain_score : "N/A",
          (app.jeeadv_rank !== null && app.jeeadv_rank !== undefined && app.jeeadv_rank !== "") ? app.jeeadv_rank : "N/A",
          (app.jeeadv_score !== null && app.jeeadv_score !== undefined && app.jeeadv_score !== "") ? app.jeeadv_score : "N/A",
          app.bjr ?? "",
          getOnlineField(app, 'online_round_token') || app.iir || "",
          getOnlineField(app, 'online_sliding_status') ?? "",
          status?.replace(/_/g, " ") ?? "",
          onlineEligibility,
        ];
      });

      // Sort by IIR ascending (col index 10)
      dataRows.sort((a, b) => {
        const iirA = typeof a[10] === "number" ? a[10] : Number(a[10]) || Infinity;
        const iirB = typeof b[10] === "number" ? b[10] : Number(b[10]) || Infinity;
        return iirA - iirB;
      });

      dataRows.forEach((row, idx) => { row[0] = idx + 1; });

      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      XLSX.utils.book_append_sheet(wb, ws, "Online Round Candidates");
    } else if (isBtechRound) {
      // B.Tech Offline: full column set
      const headerRow = [
        "S.No.", "Application No", "Name", "Email", "Programme",
        "JEE Main Rank (CRL)", "JEE Main Score",
        "JEE Advanced Rank (CRL)", "JEE Advanced Score",
        "JEE Main Application No", "JEE Advanced Roll No",
        "Best JEE Rank (BJR)", "Internal IIIT-B Rank (IIR)",
        "JEE Main Verification Status", "JEE Advanced Verification Status", "Sliding Status", "Offer Status",
        "Preference 1", "Preference 2", "Preference 3", "Preference 4", "Preference 5",
      ];

      const dataRows = filteredApplications.map((app: Application) => {
        const extra = app.extra_data || {};
        const matchedProgramme = extra.matched_programme || "";

        const formatStatus = (s?: string) => {
          if (!s) return "Pending";
          if (s === "ACCEPTED") return "Accepted";
          if (s === "NOT_ACCEPTED") return "Not Accepted";
          if (s === "JEE_EDITING") return "JEE Editing";
          if (s === "JEE_MAIN_RESUBMITTED") return "JEE Main Resubmitted";
          if (s === "JEE_ADVANCED_RESUBMITTED") return "JEE Advanced Resubmitted";
          return s;
        };

        const jeeMainStatus = formatStatus(app.jee_main_verification_status);
        const jeeAdvStatus = formatStatus(app.jee_advanced_verification_status);
        const status = getEffectiveStatus(app);

        return [
          0,
          app.application_number || "",
          app.fullname || "",
          app.email || "",
          matchedProgramme,
          (app.jeemain_rank !== null && app.jeemain_rank !== undefined && app.jeemain_rank !== "") ? app.jeemain_rank : "N/A",
          (app.jeemain_score !== null && app.jeemain_score !== undefined && app.jeemain_score !== "") ? app.jeemain_score : "N/A",
          (app.jeeadv_rank !== null && app.jeeadv_rank !== undefined && app.jeeadv_rank !== "") ? app.jeeadv_rank : "N/A",
          (app.jeeadv_score !== null && app.jeeadv_score !== undefined && app.jeeadv_score !== "") ? app.jeeadv_score : "N/A",
          (app.jeemain_rollno !== null && app.jeemain_rollno !== undefined && app.jeemain_rollno !== "") ? app.jeemain_rollno : "N/A",
          (app.jeeadv_rollno !== null && app.jeeadv_rollno !== undefined && app.jeeadv_rollno !== "") ? app.jeeadv_rollno : "N/A",
          app.bjr ?? "",
          app.iir ?? "",
          jeeMainStatus,
          jeeAdvStatus,
          app.current_sliding_status ?? "",
          status?.replace(/_/g, " ") ?? "",
          (app.pref1 !== null && app.pref1 !== undefined && app.pref1 !== "") ? app.pref1 : "N/A",
          (app.pref2 !== null && app.pref2 !== undefined && app.pref2 !== "") ? app.pref2 : "N/A",
          (app.pref3 !== null && app.pref3 !== undefined && app.pref3 !== "") ? app.pref3 : "N/A",
          (app.pref4 !== null && app.pref4 !== undefined && app.pref4 !== "") ? app.pref4 : "N/A",
          (app.pref5 !== null && app.pref5 !== undefined && app.pref5 !== "") ? app.pref5 : "N/A",
        ];
      });

      // Sort by IIR ascending (col index 12)
      dataRows.sort((a, b) => {
        const iirA = typeof a[12] === "number" ? a[12] : Number(a[12]) || Infinity;
        const iirB = typeof b[12] === "number" ? b[12] : Number(b[12]) || Infinity;
        return iirA - iirB;
      });

      // Renumber
      dataRows.forEach((row, idx) => { row[0] = idx + 1; });

      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      XLSX.utils.book_append_sheet(wb, ws, "Applications");
    } else if (isMtechRound && selectRoundType === "Online") {
      const headerRow = [
        "S.No.", "Application No.", "User Name", "Email", "Programme", "Applied Programmes",
        "Gate Score", "Gate Registration No", "Year", "Offer Status", "Online Round Eligibility",
        "College Name", "Degree", "Major", "Percentage", "Year of Passing", "Marks Obtained", "Maximum Marks"
      ];
      const dataRows = filteredApplications.map((app: Application) => {
        const extra = app.extra_data || {};
        const matchedProgramme = app.online_round_allocated_programme ? app.online_round_allocated_programme.replace(/_/g, " ") : "—";
        const appliedProgrammes = [app.pref1, app.pref2, app.pref3, app.pref4, app.pref5]
          .filter(Boolean)
          .map((p: any) => p.replace("MTech", "M.Tech").replace(/_/g, " "))
          .join(", ") || "—";

        const getMatchedPrefIndex = (app: Application) => {
          const normalize = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
          const matched = app.extra_data?.matched_programme;
          if (!matched) return null;
          if (normalize(matched) === normalize(app.pref1)) return 1;
          if (normalize(matched) === normalize(app.pref2)) return 2;
          if (normalize(matched) === normalize(app.pref3)) return 3;
          return null;
        };

        const prefIndex = getMatchedPrefIndex(app);

        let gateScore: any = "—";
        let gateRollNo: any = "—";
        let gateYear: any = "—";

        const validMatchedScore = (extra.matched_score !== undefined && Number(extra.matched_score) !== 0) ? extra.matched_score : undefined;

        if (validMatchedScore !== undefined) {
          gateScore = validMatchedScore;
          for (let i = 1; i <= 5; i++) {
            if (Number(app[`pref${i}score`]) === Number(validMatchedScore)) {
              gateRollNo = app[`pref${i}rollno`] || "—";
              gateYear = app[`pref${i}year`] || "—";
              break;
            }
          }
        } else if (prefIndex) {
          gateScore = app[`pref${prefIndex}score`] || "—";
          gateRollNo = app[`pref${prefIndex}rollno`] || "—";
          gateYear = app[`pref${prefIndex}year`] || "—";
        } else {
          let maxScore = -1;
          let bestIdx = -1;
          for (let i = 1; i <= 5; i++) {
            const s = Number(app[`pref${i}score`]);
            if (!isNaN(s) && s > maxScore) {
              maxScore = s;
              bestIdx = i;
            }
          }
          if (bestIdx !== -1) {
            gateScore = maxScore;
            gateRollNo = app[`pref${bestIdx}rollno`] || "—";
            gateYear = app[`pref${bestIdx}year`] || "—";
          }
        }

        const status = getEffectiveStatus(app);
        const onlineEligibility = getOnlineField(app, 'online_round_status')?.replace(/_/g, " ") || "NOT INVITED";

        return [
          0,
          app.application_number || "",
          app.fullname || "",
          app.email || "",
          matchedProgramme,
          appliedProgrammes,
          gateScore,
          gateRollNo,
          gateYear,
          status?.replace(/_/g, " ") ?? "",
          onlineEligibility,
          app.collegename || "",
          app.degree || "",
          app.major || "",
          app.cgpa !== undefined && app.cgpa !== null ? app.cgpa : "",
          app.passingyear || "",
          app.marks_obtained !== undefined && app.marks_obtained !== null ? app.marks_obtained : "",
          app.maximum_marks !== undefined && app.maximum_marks !== null ? app.maximum_marks : ""
        ];
      });

      // Keep original order from table, just renumber
      dataRows.forEach((row, idx) => { row[0] = idx + 1; });
      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      XLSX.utils.book_append_sheet(wb, ws, "Online Round Candidates");
    } else {
      // MTech flow: programme-wise sheets + Multiple Offers sheet
      const headerRow = ["S.No.", "Application No", "Name", "Email", "Programme", "Gate Score", "Gate Roll No", "Offer Status"];
      const sheetsData: Record<string, any[][]> = {};

      filteredApplications.forEach((app: Application) => {
        const extra = app.extra_data || {};
        const matchedProgramme = extra.matched_programme || "";
        const prefIndex = getPrefIndex(app);

        let sheetName = matchedProgramme
          ? String(matchedProgramme).substring(0, 31).replace(/[\\/*?:[\]]/g, "")
          : "No Preference";
        if (!sheetName) sheetName = "No Preference";

        if (!sheetsData[sheetName]) sheetsData[sheetName] = [headerRow];

        const serialNo = sheetsData[sheetName].length;
        const gateScore = prefIndex ? (app[`pref${prefIndex}score`] ?? "") : "";
        const gateRollNo = prefIndex ? (app[`pref${prefIndex}rollno`] ?? "") : "";
        const status = getEffectiveStatus(app);

        sheetsData[sheetName].push([
          serialNo,
          app.application_number || "",
          app.fullname || "",
          app.email || "",
          matchedProgramme,
          gateScore,
          gateRollNo,
          status?.replace(/_/g, " ") ?? ""
        ]);
      });

      // Build Multiple Offers sheet
      const appProgrammeMap: Record<string, { name: string; email: string; programmes: string[] }> = {};
      filteredApplications.forEach((app: Application) => {
        const appNo = app.application_number || "";
        const matchedProgramme = app.extra_data?.matched_programme || "";
        if (!appNo || !matchedProgramme) return;

        if (!appProgrammeMap[appNo]) {
          appProgrammeMap[appNo] = { name: app.fullname || "", email: app.email || "", programmes: [] };
        }
        if (!appProgrammeMap[appNo].programmes.includes(matchedProgramme)) {
          appProgrammeMap[appNo].programmes.push(matchedProgramme);
        }
      });

      const multiOfferHeader = ["S.No.", "Application No", "Name", "Email", "Programme 1", "Programme 2", "Programme 3"];
      const multiOfferRows: any[][] = [];
      Object.entries(appProgrammeMap).forEach(([appNo, { name, email, programmes }]) => {
        if (programmes.length <= 1) return;
        multiOfferRows.push([
          multiOfferRows.length + 1,
          appNo,
          name,
          email,
          programmes[0] || "",
          programmes[1] || "",
          programmes[2] || "",
        ]);
      });

      if (multiOfferRows.length > 0) {
        sheetsData["Multiple Offers"] = [multiOfferHeader, ...multiOfferRows];
      }

      // Sort and append each sheet
      Object.keys(sheetsData).forEach((sheetName) => {
        if (sheetName === "Multiple Offers") return;
        const rows = sheetsData[sheetName];
        if (rows.length <= 1) return;
        const header = rows[0];
        const dataRows = rows.slice(1);

        // Sort by GATE score (col index 5) descending
        dataRows.sort((a: any[], b: any[]) => {
          const scoreA = typeof a[5] === "number" ? a[5] : Number(a[5]) || 0;
          const scoreB = typeof b[5] === "number" ? b[5] : Number(b[5]) || 0;
          return scoreB - scoreA;
        });

        // Renumber
        dataRows.forEach((row: any[], idx: number) => { row[0] = idx + 1; });
        sheetsData[sheetName] = [header, ...dataRows];
      });

      // Append all sheets to workbook
      Object.keys(sheetsData).forEach((sheetName) => {
        const ws = XLSX.utils.aoa_to_sheet(sheetsData[sheetName]);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    }

    const roundObj = rounds.find(r => r.id === selectedRound);
    const roundName = roundObj?.name?.replace(/\s+/g, "_") || selectedRound;

    XLSX.writeFile(
      wb,
      `${roundName}_Export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };
  // Popup logic
  const mapFlatToSectionsMtech = (app: Record<string, any>) => {
    const mapped: Record<string, any> = {
      personal_details: {},
      current_address: {},
      permanent_address: {},
      guardian_details: {},
      graduation_details: {},
      programme_preference: {},
      gate_details: {},
      payment: {},
      offered_payment: {}
    };
    const PERSONAL = new Set(["fullname", "email", "mobile", "dob", "gender", "photo", "nationality", "abc_id"]);
    const CURRENT_ADDR = new Set(["line1", "line2", "city", "state", "pincode", "country"]);
    const GUARDIAN = new Set(["guardian_email", "guardian_name", "guardian_relation", "guardian_mobile"]);
    const GRAD = new Set(["collegename", "degree", "major", "cgpa", "passingyear", "marks_obtained", "maximum_marks"]);
    const PAYMENT = new Set(["transaction_id", "amount", "payment_status", "payment_date", "payment_mode", "reference_no", "status"]);
    const OFFERD_PAYMENT = new Set(["offered_payment_status", "offered_payment_gateway", "offered_payment_txnID", "offered_payment_round", "offered_payment_programme", "offered_payment_slot", "offered_payment_response_code", "offered_payment_response_desc", "offered_payment_refNo"]);

    Object.keys(app).forEach((k) => {
      if (SKIP_FIELDS.has(k)) return;
      if (PERSONAL.has(k)) { mapped["personal_details"][k] = app[k]; }
      else if (CURRENT_ADDR.has(k)) { mapped["current_address"][k] = app[k]; }
      else if (k.startsWith("p_") || k.startsWith("perm_")) { mapped["permanent_address"][k.replace(/^(p_|perm_)/, "")] = app[k]; }
      else if (GUARDIAN.has(k)) { mapped["guardian_details"][k] = app[k]; }
      else if (GRAD.has(k) || k.startsWith("ug_")) { mapped["graduation_details"][k.replace("ug_", "")] = app[k]; }
      else if (k.startsWith("pref")) {
        if (k.includes("score") || k.includes("rank") || k.includes("rollno") || k.includes("year") || k.includes("subjects") || k.includes("scorecard")) {
          mapped["gate_details"][k] = app[k];
        } else {
          mapped["programme_preference"][k] = app[k];
        }
      }
      else if (PAYMENT.has(k)) { mapped["payment"][k] = app[k]; }
      else if (k === "offered_payment_date") {
        mapped["offered_payment"][k] = moment(app[k])?.format("DD-MM-YYYY hh:mm A") ?? "NA";
      }
      else if (k === "offered_payment_refNo_list") {
        mapped["offered_payment"][k] = app[k].join(", \n") ?? "NA";
      }
      else if (OFFERD_PAYMENT.has(k)) {
        if (k === "offered_payment_round" && selectRoundType === "Online") {
          const roundName = rounds.find(r => r.id === app[k])?.name;
          mapped["offered_payment"][k] = roundName || app[k];
        } else {
          mapped["offered_payment"][k] = app[k];
        }
      }
    });
    return mapped;
  };

  const mapFlatToSectionsBtech = (app: Record<string, any>) => {
    const mapped: Record<string, any> = {
      personal_details: {},
      current_address: {},
      permanent_address: {},
      guardian_details: {},
      education_details: {},
      exam_details: {},
      programme_preference: {},
      payment: {},
      offered_payment: {}
    };
    const PERSONAL = new Set(["fullname", "email", "mobile", "dob", "gender", "photo", "nationality", "abc_id"]);
    const CURRENT_ADDR = new Set(["line1", "line2", "city", "state", "pincode", "country"]);
    const GUARDIAN = new Set(["guardian_email", "guardian_name", "guardian_relation", "guardian_mobile"]);
    const PAYMENT = new Set(["transaction_id", "amount", "payment_status", "payment_date", "payment_mode", "reference_no", "status"]);
    const PREF_KEYS = new Set(["pref1", "pref2", "pref3", "pref4", "pref5"]);
    const OFFERD_PAYMENT = new Set(["offered_payment_status", "offered_payment_gateway", "offered_payment_txnID", "offered_payment_round", "offered_payment_programme", "offered_payment_slot", "offered_payment_response_code", "offered_payment_response_desc", "offered_payment_refNo"]);

    Object.keys(app).forEach((k) => {
      if (SKIP_FIELDS.has(k)) return;
      if (PERSONAL.has(k)) { mapped["personal_details"][k] = app[k]; }
      else if (CURRENT_ADDR.has(k)) { mapped["current_address"][k] = app[k]; }
      else if (k.startsWith("p_") || k.startsWith("perm_")) { mapped["permanent_address"][k.replace(/^(p_|perm_)/, "")] = app[k]; }
      else if (GUARDIAN.has(k)) { mapped["guardian_details"][k] = app[k]; }
      else if (k.startsWith("edu10_") || k.startsWith("edu12_")) { mapped["education_details"][k] = app[k]; }
      else if (k.startsWith("jeemain_") || k.startsWith("jeeadv_") || k.startsWith("olympiad_")) { mapped["exam_details"][k] = app[k]; }
      else if (PREF_KEYS.has(k)) { mapped["programme_preference"][k] = app[k]; }
      else if (PAYMENT.has(k)) { mapped["payment"][k] = app[k]; }
      else if (k === "offered_payment_date") {
        mapped["offered_payment"][k] = moment(app[k])?.format("DD-MM-YYYY hh:mm A") ?? "NA";
      }
      else if (k === "offered_payment_refNo_list") {
        mapped["offered_payment"][k] = app[k].join(", \n") ?? "NA";
      }
      else if (OFFERD_PAYMENT.has(k)) {
        if (k === "offered_payment_round" && selectRoundType === "Online") {
          const roundName = rounds.find(r => r.id === app[k])?.name;
          mapped["offered_payment"][k] = roundName || app[k];
        } else {
          mapped["offered_payment"][k] = app[k];
        }
      }
    });
    return mapped;
  };

  const fetchApplicationDetails = async (userId: string, appNumber: string) => {
    setPopupAppNumber(appNumber);
    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);
    const isBtechPopup = isBtechRound;
    setPopupIsBtech(isBtechPopup);
    setExpandedSections(new Set(
      isBtechPopup
        ? ["personal_details", "education_details", "exam_details", "programme_preference"]
        : ["personal_details", "graduation_details", "gate_details", "programme_preference"]
    ));

    try {
      const dbQuery = `queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${userId}`;
      const url = `${apiConfig.getResourceUrl("applications")}?${dbQuery}`;
      const response = await apiFetch(url);
      if (!response.ok) throw new Error("Failed to fetch app details");

      const data = await response.json();
      let appData: Application | null = null;

      if (Array.isArray(data)) {
        appData = (data as Application[]).find(a => a.application_number === appNumber) || null;

      } else if (data?.resource?.length > 0) {
        appData = (data.resource as Application[]).find(a => a.application_number === appNumber) || null;

      } else if (data?.records?.length > 0) {
        appData = (data.records as Application[]).find(a => a.application_number === appNumber) || null;
      }

      if (appData) {
        setPopupData(isBtechPopup ? mapFlatToSectionsBtech(appData) : mapFlatToSectionsMtech(appData));
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not load application details");
    } finally {
      setPopupLoading(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const formatFieldLabel = (key: string) => {
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatFieldValue = (key: string, val: any, isExport: boolean = false): any => {
    if (val === null || val === undefined || val === "") return isExport ? "" : "—";

    if (key === "dob") {
      try {
        const d = new Date(typeof val === "number" ? val : String(val));
        if (!isNaN(d.getTime())) {
          return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
        }
      } catch { }
    }

    // 🔥 IMPORTANT: export should NOT return JSX
    if (["photo", "scorecard", "pref1scorecard", "pref2scorecard", "pref3scorecard"].includes(key)) {
      if (!val) return isExport ? "" : "NA";

      if (isExport) {
        return val?.url || val?.path || String(val); // return URL/string for Excel
      }

      return <RenderImage photo={val} key={key} />;
    }

    if (typeof val === "object" && val !== null && !(val instanceof Array)) {
      const str = String(val);
      if (str.includes("T")) return str.split("T")[0];
      return isExport ? JSON.stringify(val) : JSON.stringify(val);
    }

    return String(val);
  };

  return (
    <div className="dash-layout">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p) => !p)}
      />

      <div className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""}`}>
        <header className="dash-header">
          <h1 className="dash-page-title">Rounds</h1>
          <div className="dash-profile-sec">
            {/* {totalCount > 0 && <div className="badge bg-light text-primary me-3">{totalCount} Candidates</div>} */}
            <div className="dash-user-profile">
              <div className="dash-profile-circle" onClick={() => setShowDropdown((p) => !p)}>
                <span>A</span>
              </div>
              {showDropdown && (
                <div className="dash-profile-dropdown">
                  <LogoutButton />
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dash-content-body">
          <div className="app-table-container">
            <div className="app-table-header" style={{ alignItems: "center", marginBottom: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <h3>Applications</h3>
                <span className="app-count">{totalCount}</span>
              </div>

              <select
                className="form-select border-primary bg-primary text-white shadow-sm"
                value={selectRoundType}
                onChange={onRoundTypeChange}
                style={{ flex: '0 1 250px', borderRadius: '6px', height: '36px', fontSize: '12px', fontWeight: '500', marginLeft: 50 }}
              >
                <option value="">Choose Round Type</option>
                <option key={"Online"} value={"Online"}>ONLINE</option>
                <option key={"Offline"} value={"Offline"}>OFFLINE</option>
                <option key={"Manual"} value={"Manual"}>MANUAL</option>
              </select>

            </div>

            <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e5e7eb", padding: "0 4px" }}>
              {[
                { id: "ELIGIBLE", label: "Eligible Candidates", count: tabCounts.ELIGIBLE },
                { id: "REGISTERED", label: "Registered List", count: tabCounts.REGISTERED },
                { id: "TOKEN_EMAIL_SEND", label: "Send Token Email", count: tabCounts.TOKEN_EMAIL_SEND },
                { id: "POOL", label: "Confirm Offer List", count: tabCounts.POOL },
                { id: "PENDING_GEN", label: "Generate Offer Letter", count: tabCounts.PENDING_GEN },
                { id: "UPGRADED", label: isMtechRound ? "Paid Offer List" : "Upgraded Offer List", count: tabCounts.UPGRADED },
                { id: "PENDING_SEND", label: "Send Offer Letter", count: tabCounts.PENDING_SEND },
                { id: "SENT", label: "Offers Sent", count: tabCounts.SENT },
                { id: "CANCELLED", label: "Offer Cancelled", count: tabCounts.CANCELLED },
              ]
                .filter((tab) => {
                  if (tab.id === "ELIGIBLE" || tab.id === "REGISTERED") {
                    return selectRoundType === "Online";
                  }
                  // TOKEN_EMAIL_SEND tab only for BTech R3 and MTech R2 online
                  if (tab.id === "TOKEN_EMAIL_SEND") {
                    return selectRoundType === "Online" && isTwoStepTokenRound;
                  }
                  if (tab.id === "UPGRADED") {
                    if (selectRoundType === "Online") return true;
                    if (isBtechRound) return isSlidingRound;
                    if (isMtechRound) return !isFirstRound;
                    return false;
                  }
                  return true;
                })
                .map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id as any); setPageNo(1); }}
                    style={{
                      padding: "8px 4px",
                      fontSize: "14px",
                      fontWeight: activeTab === tab.id ? 600 : 500,
                      color: activeTab === tab.id ? "#2563eb" : "#6b7280",
                      border: "none",
                      background: "none",
                      borderBottom: activeTab === tab.id ? "2px solid #2563eb" : "2px solid transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    {tab.label}
                    <span style={{
                      fontSize: "11px",
                      backgroundColor: activeTab === tab.id ? "#dbeafe" : "#f3f4f6",
                      color: activeTab === tab.id ? "#1e40af" : "#4b5563",
                      padding: "2px 6px",
                      borderRadius: "10px"
                    }}>
                      {tab.count}
                    </span>
                  </button>
                ))}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "16px", flexWrap: "nowrap", width: '100%', padding: "0 4px" }}>
              <input
                type="text"
                className="form-control bg-white shadow-sm"
                placeholder="Search by App. No, Name or Email"
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPageNo(1); }}
                style={{
                  padding: "6px 10px", borderRadius: "6px",
                  border: "1px solid #d1d5db", fontSize: "13px",
                  color: "#374151", backgroundColor: "#fff", flex: '0 1 250px'
                }}
              />
              {activeTab === "SENT" && (
                <select
                  className="form-select shadow-sm"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPageNo(1); }}
                  style={{ flex: '0 1 180px', borderRadius: '6px', height: '36px', fontSize: '12px', border: '1px solid #d1d5db', color: '#374151' }}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="OFFER_SENT">Offer Sent</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </select>
              )}
              {activeTab === "SENT" && (
                <select
                  className="form-select shadow-sm"
                  value={offeredPaymentStatusFilter}
                  onChange={(e) => { setOfferedPaymentStatusFilter(e.target.value); setPageNo(1); }}
                  style={{ flex: '0 1 180px', borderRadius: '6px', height: '36px', fontSize: '12px', border: '1px solid #d1d5db', color: '#374151' }}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="INITIATED">Initiated</option>
                  <option value="SUCCESS">Success</option>
                  <option value="UNKNOWN">Unkown</option>
                </select>
              )}
              <select
                className="form-select border-primary bg-primary text-white shadow-sm"
                value={selectedRound}
                onChange={onRoundChange}
                style={{ flex: '0 1 250px', borderRadius: '6px', height: '36px', fontSize: '12px', fontWeight: '500' }}
              >
                <option value="">Choose Round</option>
                {rounds.filter((r) => r.round_type === selectRoundType).filter(r => r && r.id).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name || `Round ${r.id}`}{r.allocation_mode === "RECOVERY" ? " [Revised]" : r.allocation_mode === "SLIDING" ? " [Sliding]" : ""} ({formatDateDDMMYYYY(r.startdate || r.date)} to {formatDateDDMMYYYY(r.enddate || r.date)})
                  </option>
                ))}
              </select>
              <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
                <button
                  className="btn btn-outline-primary btn-sm fw-medium px-4 shadow-sm rounds-export-btn"
                  onClick={handleExport}
                  disabled={!filteredApplications.length}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "6px",
                    border: "1px solid #10b981",
                    backgroundColor: "#f0fdf4",
                    color: "#059669",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: !filteredApplications.length ? "not-allowed" : "pointer",
                    opacity: !filteredApplications.length ? 0.6 : 1,
                  }}
                >
                  Export
                </button>




                {activeTab === "ELIGIBLE" && (
                  <>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleMarkOnlineEligible}
                      disabled={selectedIds?.length === 0 || isMarkingEligible}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                    >
                      {isMarkingEligible ? "Processing..." : "Mark Online Eligible"}
                    </button>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleViewOnlineRound}
                      disabled={!selectedRound}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#10b981" }}
                    >
                      View Online Round
                    </button>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleViewSeatMatrix}
                      disabled={!hasTokensGenerated}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#6366f1" }}
                    >
                      View Seat Matrix
                    </button>
                  </>
                )}

                {activeTab === "TOKEN_EMAIL_SEND" && selectRoundType === "Online" && isTwoStepTokenRound && (
                  <>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleSendTokenEmail}
                      disabled={selectedIds?.length === 0 || isSendingTokenEmail}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#f59e0b" }}
                    >
                      {isSendingTokenEmail ? "Sending..." : "Send Token Email"}
                    </button>
                  </>
                )}

                {activeTab === "REGISTERED" && selectRoundType === "Online" && (
                  <>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleGenerateTokens}
                      disabled={selectedIds?.length === 0 || isGeneratingTokens}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                    >
                      {isGeneratingTokens ? "Generating..." : "Generate Token"}
                    </button>

                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleViewOnlineRound}
                      disabled={!selectedRound}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#10b981" }}
                    >
                      View Online Round
                    </button>
                    <button
                      className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                      onClick={handleViewSeatMatrix}
                      disabled={!hasTokensGenerated}
                      style={{ borderRadius: "6px", height: "36px", backgroundColor: "#6366f1" }}
                    >
                      View Seat Matrix
                    </button>
                  </>
                )}

                {activeTab === "POOL" && (
                  <>
                    {selectRoundType === "Online" ? (
                      <>
                        <button
                          className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                          onClick={handleViewOnlineRound}
                          disabled={!selectedRound}
                          style={{ borderRadius: "6px", height: "36px", backgroundColor: "#10b981" }}
                        >
                          View Online Round
                        </button>
                        <button
                          className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                          onClick={handleViewSeatMatrix}
                          disabled={!hasTokensGenerated}
                          style={{ borderRadius: "6px", height: "36px", backgroundColor: "#6366f1" }}
                        >
                          View Seat Matrix
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                        onClick={handleConfirmOffer}
                        disabled={selectedRoundType === "Online" || isConfirming || !selectedRound || selectedIds.length === 0}
                        style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                      >
                        {isConfirming ? "Confirming..." : "Confirm Selection"}
                      </button>
                    )}
                  </>
                )}

                {activeTab === "SENT" && <button
                  className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                  onClick={handleSendEmail}
                  disabled={selectedIds?.length === 0}
                  style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                >
                  {isSendingEmail ? "Confirming..." : "Send Message"}
                </button>}

                {activeTab === "UPGRADED" && <button
                  className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                  onClick={() => {
                    if (isMtechRound) {
                      handleSendEmail();
                    } else {
                      setShowUpgradeConfirmModal(true);
                    }
                  }}
                  disabled={selectedIds?.length === 0}
                  style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                >
                  {isMtechRound ? "Send Offer Notification" : "Send Upgrade Notification"}
                </button>}

                <SendEmail
                  show={sendEmailOpen}
                  onClose={() => setSendEmailOpen(false)}
                  selectedEmails={selectedEmails}
                  setIsSendingEmail={setIsSendingEmail}
                  onSuccess={() => {
                    if (activeTab === "UPGRADED" && isMtechRound) {
                      executeMarkMtechPaidOfferSent();
                    }
                  }}
                />

                {/* ── Upgrade Notification Confirmation Modal ── */}
                {showUpgradeConfirmModal && (
                  <div style={{
                    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
                    zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center"
                  }}>
                    <div style={{
                      background: "#fff", borderRadius: "12px", padding: "28px 32px",
                      maxWidth: "480px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
                    }}>
                      <h5 style={{ fontWeight: 700, marginBottom: "12px" }}>Confirm Upgrade Notification</h5>
                      <p style={{ color: "#555", marginBottom: "20px", lineHeight: 1.6 }}>
                        An upgrade notification email will be sent automatically to <strong>{selectedIds.length}</strong> candidate(s).
                        This action cannot be undone.
                      </p>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowUpgradeConfirmModal(false)}
                          style={{ borderRadius: "6px", minWidth: "90px" }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary btn-sm text-white"
                          style={{ borderRadius: "6px", minWidth: "130px", backgroundColor: "#3b82f6", border: "none" }}
                          onClick={async () => {
                            setShowUpgradeConfirmModal(false);
                            await executeMarkUpgradeSent();
                          }}
                        >
                          Confirm &amp; Send
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {(activeTab === "PENDING_GEN") && (
                  <button
                    className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                    onClick={handleGenerateOffer}
                    disabled={isGenerating || isSending || !selectedRound || selectedIds.length === 0}
                    style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                  >
                    {(isGenerating ? "Generating..." : "Generate Offer Letter")}
                  </button>
                )}

                {activeTab === "PENDING_SEND" && (
                  <button
                    className="btn btn-success btn-sm btn-accept fw-medium px-4 text-white border-0 shadow-sm"
                    onClick={handleSendOffer}
                    disabled={isGenerating || isSending || !selectedRound || selectedIds.length === 0}
                    style={{ borderRadius: "6px", height: "36px" }}
                  >
                    {isSending ? "Sending..." : "Send Offer Letter"}
                  </button>
                )}

                {activeTab === "SENT" && (
                  <button
                    className="btn btn-danger btn-sm fw-medium px-4 text-white border-0 shadow-sm"
                    onClick={handleCancelOffer}
                    disabled={isGenerating || isSending || isCancelling || !selectedRound || selectedIds.length === 0}
                    style={{ borderRadius: "6px", height: "36px", backgroundColor: "#ef4444" }}
                  >
                    {isCancelling ? "Cancelling..." : "Cancel Offer"}
                  </button>
                )}
              </div>
            </div>

            {isGenerating && genProgress && (
              <div style={{ width: '100%', padding: '0 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  <span>Generating Offer Letters...</span>
                  <span>{Math.round((genProgress.done / genProgress.total) * 100)}%</span>
                </div>
                <div style={{ background: '#e5e7eb', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                  <div style={{
                    background: '#3b82f6', height: '10px',
                    width: `${Math.round((genProgress.done / genProgress.total) * 100)}%`,
                    transition: 'width 0.4s ease'
                  }} />
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  Processed {genProgress.done} of {genProgress.total} candidates. {genProgress.failed > 0 && <span style={{ color: '#ef4444' }}>({genProgress.failed} failed)</span>}
                </p>
              </div>
            )}

            {isSending && sendProgress && (
              <div style={{ width: '100%', padding: '0 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  <span>Sending Offer Letters...</span>
                  <span>{Math.round((sendProgress.done / sendProgress.total) * 100)}%</span>
                </div>
                <div style={{ background: '#e5e7eb', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                  <div style={{
                    background: '#10b981', height: '10px',
                    width: `${Math.round((sendProgress.done / sendProgress.total) * 100)}%`,
                    transition: 'width 0.4s ease'
                  }} />
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  Processed {sendProgress.done} of {sendProgress.total} candidates. {sendProgress.failed > 0 && <span style={{ color: '#ef4444' }}>({sendProgress.failed} failed)</span>}
                </p>
              </div>
            )}

            {loading ? (
              <div className="app-loading">Loading applications...</div>
            ) : applications.length === 0 || totalCount === 0 ? (
              <div className="app-empty">
                {selectedRound ? "No eligible candidates match criteria." : "Please select a round to view candidates."}
              </div>
            ) : (
              <div className="app-table-wrapper">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            (() => {
                              const currentPageApps = filteredApplications.slice((pageNo - 1) * pageSize, pageNo * pageSize).filter(a => !isRowDisabled(a));
                              return currentPageApps.length > 0 && currentPageApps.every(
                                a => {
                                  const prog = (a.extra_data && a.extra_data.matched_programme) ? a.extra_data.matched_programme : (selectRoundType === "Online" ? (getOnlineField(a, 'online_round_allocated_programme') || '') : '');
                                  return selectedIds.includes(`${a.id}|${prog}|${a.current_sliding_status || ''}`);
                                }
                              );
                            })()
                          }
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>#</th>
                      <th>Application No.</th>
                      <th>User Name</th>
                      <th>Email</th>
                      <th>Programme</th>
                      {isMtechRound && selectRoundType === "Online" && <th>Applied Programmes</th>}
                      {!isBtechRound && (
                        <>
                          <th>Gate Score</th>
                          <th>Gate Registration No</th>
                          <th>Year</th>
                        </>
                      )}
                      {isBtechRound && (
                        <>
                          <th>JEE Main Rank</th>
                          <th>JEE Advanced Rank</th>
                          {selectedRoundType === "Offline" && (
                            <>
                              <th style={{ textAlign: "center" }}>JEE Main Verification Status</th>
                              <th style={{ textAlign: "center" }}>JEE Advanced Verification Status</th>
                            </>
                          )}
                          <th>Best JEE Rank (BJR)</th>
                          <th>{selectedRoundType === "Online" ? "Rank Token Number" : "Internal IIIT-B Rank (IIR)"}</th>
                        </>
                      )}
                      {isMtechRound && selectRoundType === "Online" && <th>Rank Token Number</th>}
                      {(activeTab === "POOL" || isSlidingRound) && !isMtechRound && !isOnline && <th>Sliding Status</th>}
                      <th>Offer Status</th>
                      {["PENDING_SEND", "SENT"].includes(activeTab) && <th>Offer Letter</th>}
                      {activeTab === "CANCELLED" && <th>Cancellation Reason</th>}
                      {isOnline && (
                        <>
                          <th>Online Round Eligibility</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const pageApps = filteredApplications.slice((pageNo - 1) * pageSize, pageNo * pageSize);
                      const showProgHeaders = false;
                      const normProgLabel = (p?: string) => {
                        if (!p) return "";
                        const n = p.toLowerCase().replace(/[^a-z]/g, "");
                        if (n.includes("cse")) return "M.Tech CSE";
                        if (n.includes("ece")) return "M.Tech ECE";
                        return "M.Tech AI&DS";
                      };
                      let lastProg = "";
                      const totalCols = 10; // approximate colspan — covers all columns
                      const rows: React.ReactNode[] = [];
                      pageApps.forEach((app: Application, idx: number) => {
                        // push actual app row inline via IIFE
                        rows.push((() => {
                          const extra = app.extra_data || {};
                          const getMatchedPrefIndex = (app: Application) => {
                            const normalize = (str?: string) =>
                              str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

                            const matched = app.extra_data?.matched_programme;

                            if (!matched) return null;

                            if (normalize(matched) === normalize(app.pref1)) return 1;
                            if (normalize(matched) === normalize(app.pref2)) return 2;
                            if (normalize(matched) === normalize(app.pref3)) return 3;

                            return null;
                          };

                          const prefIndex = getMatchedPrefIndex(app);

                          let gateScore: any = "—";
                          let gateRollNo: any = "—";
                          let gateYear: any = "—";

                          const validMatchedScore = (extra.matched_score !== undefined && Number(extra.matched_score) !== 0) ? extra.matched_score : undefined;

                          if (validMatchedScore !== undefined) {
                            gateScore = validMatchedScore;
                            for (let i = 1; i <= 5; i++) {
                              if (Number(app[`pref${i}score`]) === Number(validMatchedScore)) {
                                gateRollNo = app[`pref${i}rollno`] || "—";
                                gateYear = app[`pref${i}year`] || "—";
                                break;
                              }
                            }
                          } else if (prefIndex) {
                            gateScore = app[`pref${prefIndex}score`] || "—";
                            gateRollNo = app[`pref${prefIndex}rollno`] || "—";
                            gateYear = app[`pref${prefIndex}year`] || "—";
                          } else {
                            let maxScore = -1;
                            let bestIdx = -1;
                            for (let i = 1; i <= 5; i++) {
                              const s = Number(app[`pref${i}score`]);
                              if (!isNaN(s) && s > maxScore) {
                                maxScore = s;
                                bestIdx = i;
                              }
                            }
                            if (bestIdx !== -1) {
                              gateScore = maxScore;
                              gateRollNo = app[`pref${bestIdx}rollno`] || "—";
                              gateYear = app[`pref${bestIdx}year`] || "—";
                            }
                          }

                          const getMatchedDocId = (app: Application) => {
                            if (isOnline) {
                              const normalize = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                              const matched = app.extra_data?.matched_programme;
                              
                              if (isOnlineRound5) return app.online_round5_offer_letter_doc_id;
                              if (isOnlineRound4) return app.online_round4_offer_letter_doc_id;
                              if (isOnlineRound3) return app.online_round3_offer_letter_doc_id;
                              
                              if (isOnlineRound2) {
                                if (matched && normalize(matched) === normalize((app as any).online_round2_allocated_programme_2)) {
                                  return (app as any).online_round2_offer_letter_doc_id_2;
                                }
                                return app.online_round2_offer_letter_doc_id;
                              }
                              
                              if (matched && normalize(matched) === normalize((app as any).online_round_allocated_programme_2)) {
                                return (app as any).online_round_offer_letter_doc_id_2;
                              }
                              return app.online_round_offer_letter_doc_id;
                            }
                            const normalize = (str?: string) => str?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                            const matched = app.extra_data?.matched_programme;
                            if (!matched) return null;
                            if (normalize(matched) === normalize(app.offered_programme_1)) return app.offer_letter_doc_id_1;
                            if (normalize(matched) === normalize(app.offered_programme_2)) return app.offer_letter_doc_id_2;
                            if (normalize(matched) === normalize(app.offered_programme_3)) return app.offer_letter_doc_id_3;
                            return app.offer_letter_doc_id;
                          };

                          const disabled = isRowDisabled(app);
                          return (
                            <tr
                              key={`${app.id}-${extra.matched_programme || idx}`}
                              style={disabled ? { opacity: 0.55, backgroundColor: '#f9fafb' } : {}}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  disabled={disabled}
                                  checked={!disabled && selectedIds.includes(`${app.id}|${(app.extra_data && app.extra_data.matched_programme) ? app.extra_data.matched_programme : (selectRoundType === "Online" ? (getOnlineField(app, 'online_round_allocated_programme') || '') : '')}|${app.current_sliding_status || ''}`)}
                                  onChange={() => !disabled && handleRowSelect(app.id, (app.extra_data && app.extra_data.matched_programme) ? app.extra_data.matched_programme : (selectRoundType === "Online" ? (getOnlineField(app, 'online_round_allocated_programme') || '') : ''), app.current_sliding_status || '')}
                                />
                              </td>
                              <td>{(pageNo - 1) * pageSize + idx + 1}</td>
                              <td>
                                <button
                                  className="app-number-link"
                                  onClick={() => fetchApplicationDetails(app.user_id, app.application_number)}
                                  title="View full details"
                                >
                                  {app.application_number}
                                </button>
                              </td>
                              <td>{app.fullname || "—"}</td>
                              <td>{app.email || "—"}</td>
                              <td className="fw-bold text-bold">
                                {extra.matched_programme
                                  ? (extra.matched_programme === "MTech_AI_DS" || extra.matched_programme === "MTech_AI&DS")
                                    ? "M.Tech AI&DS"
                                    : extra.matched_programme
                                      .replace("MTech", "M.Tech")
                                      .replace(/_/g, " ")
                                  : selectRoundType === "Online"
                                    ? (() => {
                                      const prog1 = getOnlineField(app, 'online_round_allocated_programme');
                                      const prog2 = (isOnlineRound2 || isOnlineRound3 || isOnlineRound4 || isOnlineRound5) ? null : (app as any).online_round_allocated_programme_2;
                                      if (!prog1) return "—";
                                      const fmt = (p: string) => p.replace(/_/g, " ");
                                      return prog2
                                        ? <>{fmt(prog1)}<br /><span style={{ fontSize: '11px', color: '#6366f1', fontWeight: 500 }}>{fmt(prog2)}</span></>
                                        : fmt(prog1);
                                    })()
                                    : "—"}
                              </td>
                              {isMtechRound && selectRoundType === "Online" && (
                                <td style={{ fontSize: "13px" }}>
                                  {[app.pref1, app.pref2, app.pref3, app.pref4, app.pref5]
                                    .filter(Boolean)
                                    .map((p: any) => p.replace("MTech", "M.Tech").replace(/_/g, " "))
                                    .join(", ") || "—"}
                                </td>
                              )}
                              {!isBtechRound && (
                                <>
                                  <td>{gateScore}</td>
                                  <td>{gateRollNo}</td>
                                  <td>{gateYear}</td>
                                </>
                              )}
                              {isBtechRound && (
                                <>
                                  <td>{app.jeemain_rank !== null && app.jeemain_rank !== undefined && app.jeemain_rank !== "" ? app.jeemain_rank : "N/A"}</td>
                                  <td>{app.jeeadv_rank !== null && app.jeeadv_rank !== undefined && app.jeeadv_rank !== "" ? app.jeeadv_rank : "N/A"}</td>
                                  {selectedRoundType === "Offline" && (
                                    <>
                                      <td style={{ textAlign: "center" }}>
                                        {renderCircleStatus(app.jee_main_verification_status)}
                                      </td>
                                      <td style={{ textAlign: "center" }}>
                                        {renderCircleStatus(app.jee_advanced_verification_status)}
                                      </td>
                                    </>
                                  )}
                                  <td>{app.bjr !== null && app.bjr !== undefined && app.bjr !== "" ? app.bjr : "—"}</td>
                                  <td>{(() => {
                                    if (selectedRoundType === "Online") {
                                      const token = getOnlineField(app, 'online_round_token');
                                      return token !== null && token !== undefined && token !== "" ? token : (app.iir !== null && app.iir !== undefined && app.iir !== "" ? app.iir : "—");
                                    }
                                    return app.iir !== null && app.iir !== undefined && app.iir !== "" ? app.iir : "—";
                                  })()}</td>
                                </>
                              )}
                              {isMtechRound && selectRoundType === "Online" && (
                                <td style={{ fontWeight: 600 }}>
                                  {app.iir !== null && app.iir !== undefined && app.iir !== ""
                                    ? app.iir
                                    : (getOnlineField(app, 'online_round_token') || "—")}
                                </td>
                              )}
                              {(activeTab === "POOL" || isSlidingRound) && !isMtechRound && !isOnline && (
                                <td>
                                  {app.current_sliding_status && (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      background:
                                        app.current_sliding_status === 'SLIDE' ? '#dbeafe' :
                                          app.current_sliding_status === 'RETAINED' ? '#f3f4f6' : '#dcfce7',
                                      color:
                                        app.current_sliding_status === 'SLIDE' ? '#1d4ed8' :
                                          app.current_sliding_status === 'RETAINED' ? '#6b7280' : '#166534',
                                    }}>
                                      {app.current_sliding_status}
                                    </span>
                                  )}
                                </td>
                              )}
                              <td style={{ fontSize: '12px' }}>
                                {(() => {
                                  const status = getEffectiveStatus(app);
                                  if (!status || status === "NONE" || status === "DRAFT") return "—";
                                  return <span className={`status-badge status-${status.toLowerCase()}`} style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: status === "OFFER_SENT" ? "#dcfce7" : status === "OFFER_GENERATED" ? "#eff6ff" : "#f3f4f6",
                                    color: status === "OFFER_SENT" ? "#166534" : status === "OFFER_GENERATED" ? "#1e40af" : "#374151",
                                    fontWeight: 600
                                  }}>{status.replace(/_/g, " ")}</span>
                                })()}
                              </td>
                              {isOnline && (
                                <>
                                  <td>{getOnlineField(app, 'online_round_status')?.replace(/_/g, " ") || "NOT INVITED"}</td>
                                </>
                              )}
                              {["PENDING_SEND", "SENT"].includes(activeTab) && (
                                <td style={{ textAlign: "center" }}>
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ padding: "2px 8px", fontSize: "12px", borderRadius: "4px", minWidth: 50 }}
                                    disabled={viewingDocId === getMatchedDocId(app)}
                                    onClick={async () => {
                                      const docId = getMatchedDocId(app);
                                      if (!docId) {
                                        toast.error("Offer letter document not found");
                                        return;
                                      }
                                      setViewingDocId(docId);
                                      try {
                                        const blob = await queryAttachmentService.getAttachment(docId, user?.userId || "");
                                        const url = URL.createObjectURL(blob);
                                        window.open(url, "_blank");
                                      } catch (e) {
                                        toast.error("Failed to open offer letter");
                                      } finally {
                                        setViewingDocId(null);
                                      }
                                    }}
                                  >
                                    {viewingDocId === getMatchedDocId(app) ? "..." : "View"}
                                  </button>
                                </td>
                              )}
                              {activeTab === "CANCELLED" && (
                                <td style={{ maxWidth: '200px', position: 'relative' }}>
                                  <div
                                    style={{
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (tooltip) tooltip.style.display = 'block';
                                    }}
                                    onMouseLeave={(e) => {
                                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (tooltip) tooltip.style.display = 'none';
                                    }}
                                  >
                                    {extra.cancellation_reason || "—"}
                                  </div>
                                  {extra.cancellation_reason && (
                                    <div
                                      style={{
                                        display: 'none',
                                        position: 'absolute',
                                        backgroundColor: '#1f2937',
                                        color: '#fff',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        width: 'max-content',
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        zIndex: 1000,
                                        bottom: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                        marginBottom: '8px'
                                      }}
                                    >
                                      {extra.cancellation_reason}
                                      <div style={{
                                        position: 'absolute',
                                        bottom: '-4px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        borderWidth: '4px 4px 0',
                                        borderStyle: 'solid',
                                        borderColor: '#1f2937 transparent transparent transparent'
                                      }} />
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })()); // end IIFE for this app row
                      }); // end forEach
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && totalCount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPageNo(1); }}
                    style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", color: "#374151" }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                  <span style={{ fontSize: "13px", color: "#6b7280", marginLeft: "8px" }}>
                    Showing {Math.min((pageNo - 1) * pageSize + 1, totalCount)}–{Math.min(pageNo * pageSize, totalCount)} of {totalCount}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button
                    onClick={() => setPageNo((p) => Math.max(1, p - 1))}
                    disabled={pageNo <= 1}
                    style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", color: pageNo <= 1 ? "#9ca3af" : "#374151", backgroundColor: "#fff", cursor: pageNo <= 1 ? "not-allowed" : "pointer" }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                    Page {pageNo} of {Math.max(1, Math.ceil(totalCount / pageSize))}
                  </span>
                  <button
                    onClick={() => setPageNo((p) => p + 1)}
                    disabled={pageNo >= Math.ceil(totalCount / pageSize)}
                    style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", color: pageNo >= Math.ceil(totalCount / pageSize) ? "#9ca3af" : "#374151", backgroundColor: "#fff", cursor: pageNo >= Math.ceil(totalCount / pageSize) ? "not-allowed" : "pointer" }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="app-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="app-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="app-popup-header">
              <h2>Application Details</h2>
              <span className="app-popup-appno">{popupAppNumber}</span>
              <button className="app-popup-close" onClick={() => setShowPopup(false)}><X size={20} /></button>
            </div>
            <div className="app-popup-body">
              {popupLoading ? (
                <div className="app-loading">Loading details...</div>
              ) : !popupData || Object.keys(popupData).length === 0 ? (
                <div className="app-empty">No details found for this application.</div>
              ) : (
                Object.entries(popupIsBtech ? BTECH_SECTION_LABELS : MTECH_SECTION_LABELS).map(([sectionKey, sectionLabel]) => {
                  const sectionData = popupData[sectionKey];
                  if (!sectionData) return null;

                  const isExpanded = expandedSections.has(sectionKey);
                  const sectionFieldOrder = popupIsBtech ? BTECH_SECTION_FIELD_ORDER : MTECH_SECTION_FIELD_ORDER;
                  const orderedKeys = sectionFieldOrder[sectionKey] || [];
                  const allKeys = Object.keys(sectionData).filter((k) => !SKIP_FIELDS.has(k));
                  const sortedKeys = [...orderedKeys.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !orderedKeys.includes(k))];
                  const fields = sortedKeys.map((k) => [k, sectionData[k]] as [string, any]);

                  return (
                    <div key={sectionKey} className="app-popup-section">
                      <button className="app-popup-section-header" onClick={() => toggleSection(sectionKey)}>
                        <span>{sectionLabel}</span>
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      {isExpanded && (
                        <div className="app-popup-section-body">
                          {fields.length === 0 ? (
                            <p className="app-no-data">No data provided.</p>
                          ) : (
                            <div className="app-detail-grid">
                              {fields.map(([key, val]) => {
                                let label = key;

                                if (key.startsWith("pref1")) {
                                  label = key.replace("pref1", "Programme 1 ");
                                } else if (key.startsWith("pref2")) {
                                  label = key.replace("pref2", "Programme 2 ");
                                } else if (key.startsWith("pref3")) {
                                  label = key.replace("pref3", "Programme 3 ");
                                }

                                return (
                                  <div key={key} className="app-detail-item">
                                    <span className="app-detail-label">{formatFieldLabel(label)}</span>
                                    <span className="app-detail-value">{formatFieldValue(key, val)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {showSelectAllModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "450px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600" }}>Select Candidates</h3>
            <p style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.5" }}>
              How would you like to select the candidates?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", flexWrap: "wrap" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowSelectAllModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const paginatedApps = filteredApplications.slice((pageNo - 1) * pageSize, pageNo * pageSize);
                  const rowKeys = paginatedApps
                    .filter(app => !isRowDisabled(app))
                    .map(app => {
                      const prog = (app.extra_data && app.extra_data.matched_programme) ? app.extra_data.matched_programme : (selectRoundType === "Online" ? (getOnlineField(app, 'online_round_allocated_programme') || '') : '');
                      return `${app.id}|${prog}|${app.current_sliding_status || ''}`;
                    });

                  setSelectedIds(rowKeys); // Overwrite completely to reflect exactly the enabled rows on the current page
                  setShowSelectAllModal(false);
                }}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#3b82f6", border: "none", color: "white" }}
              >
                Current Page
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const allRowKeys = filteredApplications
                    .filter(app => !isRowDisabled(app))
                    .map(app => {
                      const prog = (app.extra_data && app.extra_data.matched_programme) ? app.extra_data.matched_programme : (selectRoundType === "Online" ? (getOnlineField(app, 'online_round_allocated_programme') || '') : '');
                      return `${app.id}|${prog}|${app.current_sliding_status || ''}`;
                    });
                  setSelectedIds(allRowKeys);
                  setShowSelectAllModal(false);
                }}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#10b981", border: "none", color: "white" }}
              >
                All Pages
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "400px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600" }}>Confirm Send Offers</h3>
            <p style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.5" }}>
              Are you sure you want to send offer letters to <strong>{selectedIds.length}</strong> selected candidates?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowConfirmModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={executeSendOffer}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Yes, Send
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmationModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "400px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600" }}>Confirm Selection</h3>
            <p style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.5" }}>
              Are you sure you want to confirm <strong>{selectedIds.length}</strong> selected candidates for the next step?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowConfirmationModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={executeConfirmOffer}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#3b82f6", border: "none" }}
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "550px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600" }}>Cancel Offer</h3>
            <p style={{ color: "#4b5563", marginBottom: "12px" }}>
              Please enter the reason for cancellation of offer(s) for <strong>{selectedIds.length}</strong> candidate(s).
            </p>
            <textarea
              className="form-control mb-3"
              rows={5}
              placeholder="Enter the reason for cancellation here"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              style={{ borderRadius: "8px", fontSize: "14px" }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowCancelModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Back
              </button>
              <button
                className="btn btn-danger"
                onClick={executeCancelOffer}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#ef4444", border: "none" }}
              >
                Cancel Offer
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenConfirmModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "450px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600", color: "#1e40af" }}>Confirm Generation</h3>
            <div style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.6" }}>
              <p>Are you sure you want to generate offer letters for <strong>{selectedIds.length}</strong> selected candidates?</p>
              <div style={{ backgroundColor: "#fff7ed", border: "1px solid #ffedd5", padding: "12px", borderRadius: "8px", color: "#9a3412", fontSize: "13px" }}>
                <strong>Important:</strong> Once offer letters are generated, you will <strong>not</strong> be able to modify the seat allocation or offer template settings for this round in the Create/Edit Rounds section.
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowGenConfirmModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { setShowGenConfirmModal(false); executeGenerateOffer(); }}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#2563eb", border: "none" }}
              >
                Yes, Generate
              </button>
            </div>
          </div>
        </div>
      )}



      {showEligibleConfirmModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "450px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600", color: "#1e40af" }}>Confirm Eligibility</h3>
            <div style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.6" }}>
              <p>Are you sure you want to mark <strong>{selectedIds.length}</strong> selected candidates as eligible for the online round?</p>
              <p>This action will also trigger an invitation email to all selected candidates.</p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowEligibleConfirmModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={executeMarkOnlineEligible}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#3b82f6", border: "none" }}
              >
                Yes, Mark Eligible
              </button>
            </div>
          </div>
        </div>
      )}

      {showTokenConfirmModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "450px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600", color: "#1e40af" }}>Confirm Token Generation</h3>
            <div style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.6" }}>
              <p>Are you sure you want to generate tokens for <strong>{selectedIds.length}</strong> selected candidates?</p>
              {isTwoStepTokenRound ? (
                <p>This action will assign them an online counseling token. <strong>No emails will be sent yet.</strong></p>
              ) : (
                <p>This action will assign them an online counseling token and send them an email notification with their token.</p>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowTokenConfirmModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={executeGenerateTokens}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#3b82f6", border: "none" }}
              >
                Yes, Generate Tokens
              </button>
            </div>
          </div>
        </div>
      )}



      {showTokenEmailConfirmModal && (
        <div className="app-popup-overlay" style={{ zIndex: 1100 }}>
          <div className="app-popup-content" style={{ maxWidth: "450px", borderRadius: "12px", padding: "24px" }}>
            <h3 style={{ marginBottom: "16px", fontSize: "1.25rem", fontWeight: "600", color: "#b45309" }}>Confirm Token Emails</h3>
            <div style={{ color: "#4b5563", marginBottom: "24px", lineHeight: "1.6" }}>
              <p>Are you sure you want to send token emails to <strong>{selectedIds.length}</strong> selected candidates?</p>
              <p>This action will email them their already generated online counseling token.</p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                className="btn btn-light"
                onClick={() => setShowTokenEmailConfirmModal(false)}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500" }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={executeSendTokenEmail}
                style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: "500", backgroundColor: "#f59e0b", border: "none" }}
              >
                Yes, Send Emails
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
